import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { CopyIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "../../contexts/AuthContext";
import { useGuestUsage, GuestToolType } from "../../contexts/GuestUsageContext";
import { toast } from "sonner";
import ComponentCard from "./ComponentCard";
import ComponentDialog from "./ComponentDialog";
import CategoryFilters from "./CategoryFilters";
import SearchAndViewOptions from "./SearchAndViewOptions";
import { componentCategories, components } from "./componentData";
import { ComponentItem, ComplexityLevel, ViewMode, AppUiTemplate } from "./types";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { 
  Bookmark, 
  Layers, 
  Plus, 
  ChevronDown, 
  Code, 
  Layout, 
  Grid, 
  List, 
  X, 
  Box, 
  Lock,
  BarChart,
  ShoppingCart,
  Zap,
  MessageSquare,
  Users,
  Wrench,
  Terminal,
  Eye,
  Copy,
  Check,
  BookmarkPlus
} from "lucide-react";
import { Toggle } from "../ui/toggle";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Input } from "../ui/input";
import { Search } from "lucide-react";
import { useToast } from "../ui/use-toast";
import { Card, CardContent } from "../ui/card";

// App UI templates - these would typically be defined in a separate file
const appUiTemplates: AppUiTemplate[] = [
  {
    id: "dashboard-app",
    name: "Analytics Dashboard",
    description: "Modern analytics dashboard with sidebar navigation, stats cards, and data visualization",
    categories: ["productivity", "finance"],
    patterns: ["dashboard", "sidebar"],
    complexity: "medium" as ComplexityLevel,
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex overflow-hidden" style="max-width: 600px; height: 300px;">
        <!-- Sidebar -->
        <div class="w-1/5 bg-card border-r border-border p-3">
          <div class="mb-4">
            <div class="font-bold flex items-center gap-2 mb-3">
              <div class="w-5 h-5 rounded-md bg-indigo-500"></div>
              <span class="text-sm">TaskFlow</span>
            </div>
            <div class="w-full h-7 bg-background rounded-md flex items-center px-2 mb-5">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 text-muted-foreground mr-1"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>
              <span class="text-xs text-muted-foreground">Search</span>
            </div>
          </div>
          
          <div class="space-y-1 mb-5">
            <div class="px-2 py-1 rounded-md text-xs flex items-center gap-2 bg-primary/20 text-primary">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path></svg>
              Dashboard
            </div>
            <div class="px-2 py-1 rounded-md text-xs flex items-center gap-2 text-muted-foreground hover:bg-muted/50">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              Team
            </div>
            <div class="px-2 py-1 rounded-md text-xs flex items-center gap-2 text-muted-foreground hover:bg-muted/50">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M13 5v2"></path><path d="M13 17v2"></path><path d="M13 11v2"></path></svg>
              Calendar
            </div>
          </div>
          
          <div class="pt-3 border-t border-border">
            <div class="text-xs text-muted-foreground mb-2">Projects</div>
            <div class="space-y-1">
              <div class="px-2 py-1 rounded-md text-xs flex justify-between items-center text-foreground hover:bg-muted/50">
                <div class="flex items-center gap-2">
                  <div class="h-2 w-2 rounded-full bg-green-500"></div>
                  Marketing
                </div>
                <div class="text-[10px] bg-muted rounded-full px-1.5">3</div>
              </div>
              <div class="px-2 py-1 rounded-md text-xs flex justify-between items-center text-foreground hover:bg-muted/50">
                <div class="flex items-center gap-2">
                  <div class="h-2 w-2 rounded-full bg-blue-500"></div>
                  Website
                </div>
                <div class="text-[10px] bg-muted rounded-full px-1.5">7</div>
              </div>
              <div class="px-2 py-1 rounded-md text-xs flex justify-between items-center text-foreground hover:bg-muted/50">
                <div class="flex items-center gap-2">
                  <div class="h-2 w-2 rounded-full bg-amber-500"></div>
                  Product
                </div>
                <div class="text-[10px] bg-muted rounded-full px-1.5">5</div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Main content -->
        <div class="w-4/5 bg-background flex flex-col overflow-auto">
          <div class="py-3 px-4 border-b border-border flex justify-between items-center">
            <h1 class="font-bold">Dashboard</h1>
            <div class="flex items-center gap-3">
              <div class="bg-card px-2 py-1 rounded-md text-xs flex items-center gap-1 border border-border">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" x2="12" y1="15" y2="3"></line></svg>
                Share
              </div>
              <div class="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-medium">JD</div>
            </div>
          </div>
          
          <div class="p-4 grid grid-cols-3 gap-4">
            <div class="bg-card border border-border rounded-lg p-3">
              <div class="flex justify-between items-center mb-4">
                <div class="text-sm font-medium">Tasks</div>
                <span class="text-sm font-bold">24</span>
              </div>
              <div class="space-y-2 mb-3">
                <div class="w-full h-1.5 bg-muted-foreground/20 rounded-full">
                  <div class="h-full w-3/4 bg-primary rounded-full"></div>
                </div>
                <div class="flex justify-between text-xs text-muted-foreground">
                  <span>18 completed</span>
                  <span>75%</span>
                </div>
              </div>
            </div>
            
            <div class="bg-card border border-border rounded-lg p-3">
              <div class="flex justify-between items-center mb-4">
                <div class="text-sm font-medium">In Progress</div>
                <span class="text-sm font-bold">6</span>
              </div>
              <div class="space-y-2 mb-3">
                <div class="w-full h-1.5 bg-muted-foreground/20 rounded-full">
                  <div class="h-full w-1/4 bg-amber-500 rounded-full"></div>
                </div>
                <div class="flex justify-between text-xs text-muted-foreground">
                  <span>6 tasks</span>
                  <span>25%</span>
                </div>
              </div>
            </div>
            
            <div class="bg-card border border-border rounded-lg p-3">
              <div class="flex justify-between items-center mb-4">
                <div class="text-sm font-medium">Completed</div>
                <span class="text-sm font-bold">18</span>
              </div>
              <div class="space-y-2 mb-3">
                <div class="w-full h-1.5 bg-muted-foreground/20 rounded-full">
                  <div class="h-full w-3/4 bg-green-500 rounded-full"></div>
                </div>
                <div class="flex justify-between text-xs text-muted-foreground">
                  <span>18 tasks</span>
                  <span>75%</span>
                </div>
              </div>
            </div>
          </div>
          
          <div class="px-4 mb-4">
            <h2 class="font-medium text-sm mb-3">Recent Tasks</h2>
            <div class="bg-card border border-border rounded-lg divide-y divide-border">
              <div class="p-3 flex items-center gap-3">
                <input type="checkbox" class="h-4 w-4 rounded-sm border-muted-foreground" checked />
                <div class="flex-1">
                  <div class="text-sm line-through text-muted-foreground">Update homepage hero section</div>
                  <div class="text-xs text-muted-foreground">Website • Completed yesterday</div>
                </div>
                <div class="flex gap-1">
                  <div class="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px]">TS</div>
                  <div class="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px]">JD</div>
                </div>
              </div>
              <div class="p-3 flex items-center gap-3">
                <input type="checkbox" class="h-4 w-4 rounded-sm border-muted-foreground" />
                <div class="flex-1">
                  <div class="text-sm">Create social media campaign</div>
                  <div class="text-xs text-muted-foreground">Marketing • Due tomorrow</div>
                </div>
                <div class="flex gap-1">
                  <div class="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px]">MK</div>
                </div>
              </div>
              <div class="p-3 flex items-center gap-3">
                <input type="checkbox" class="h-4 w-4 rounded-sm border-muted-foreground" />
                <div class="flex-1">
                  <div class="text-sm">Finalize Q3 product roadmap</div>
                  <div class="text-xs text-muted-foreground">Product • Due in 3 days</div>
                </div>
                <div class="flex gap-1">
                  <div class="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px]">TS</div>
                  <div class="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px]">JD</div>
                  <div class="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px]">+2</div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    `,
    components: ["sidebar-layout", "grid-layout", "data-table"],
    inspired_by: "Notion and Linear"
  },
  {
    id: "social-media-app",
    name: "Social Media Feed",
    description: "Social media app interface with a feed of posts, stories, and interaction elements",
    categories: ["social"],
    patterns: ["feed", "profile"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden" style="max-width: 600px; height: 300px;">
        <div class="border-b border-border p-3 flex justify-between items-center bg-card">
          <div class="font-bold text-sm">SocialApp</div>
          <div class="flex gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-muted-foreground"><path d="m22 9-10 13L2 9l10-8 10 8Z"></path><path d="M12 17v-8"></path></svg>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-muted-foreground"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"></path></svg>
          </div>
        </div>
        <div class="p-3 border-b border-border overflow-x-auto">
          <div class="flex gap-4">
            <div class="flex flex-col items-center">
              <div class="w-14 h-14 rounded-full bg-primary/20 p-0.5 border-2 border-primary">
                <div class="w-full h-full rounded-full bg-muted/80 flex items-center justify-center text-primary font-medium">+</div>
              </div>
              <span class="text-xs mt-1">Your Story</span>
            </div>
            <div class="flex flex-col items-center">
              <div class="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-pink-500 p-0.5">
                <div class="w-full h-full rounded-full bg-muted flex items-center justify-center">
                  <span class="text-xs">A</span>
                </div>
              </div>
              <span class="text-xs mt-1">alex</span>
            </div>
            <div class="flex flex-col items-center">
              <div class="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-pink-500 p-0.5">
                <div class="w-full h-full rounded-full bg-muted flex items-center justify-center">
                  <span class="text-xs">J</span>
                </div>
              </div>
              <span class="text-xs mt-1">jamie</span>
            </div>
            <div class="flex flex-col items-center">
              <div class="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-pink-500 p-0.5">
                <div class="w-full h-full rounded-full bg-muted flex items-center justify-center">
                  <span class="text-xs">S</span>
                </div>
              </div>
              <span class="text-xs mt-1">sam</span>
            </div>
          </div>
        </div>
        <div class="flex-1 overflow-auto">
          <div class="border-b border-border p-3">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <div class="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <span class="text-xs">J</span>
                </div>
                <div class="text-xs font-medium">jamie</div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-muted-foreground"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>
            </div>
            <div class="aspect-square bg-muted rounded-md mb-2 flex items-center justify-center">
              <span class="text-xs text-muted-foreground">Post Image</span>
            </div>
            <div class="flex justify-between mb-1">
              <div class="flex gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path></svg>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5"><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"></path></svg>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5"><path d="M22 3 9.218 10.083"></path><path d="M11.698 20.334 22 3.001"></path><path d="M9.218 10.084 11.698 20.334"></path><path d="M15.5 10.5 11.698 20.334"></path><path d="M15.5 10.5 9.219 10.083"></path></svg>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5"><path d="M17.5 3A2.5 2.5 0 0 1 20 5.5v13a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 18.5v-13A2.5 2.5 0 0 1 6.5 3h11Z"></path><path d="M9 10h6"></path><path d="M12 7v6"></path><path d="M9 17h6"></path></svg>
            </div>
            <div class="text-xs font-medium">1,234 likes</div>
            <div class="text-xs"><span class="font-medium">jamie</span> Beautiful sunset today! #nature #photography</div>
          </div>
        </div>
        <div class="border-t border-border p-2 flex justify-between bg-card">
          <button class="p-1.5"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg></button>
          <button class="p-1.5"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg></button>
          <button class="p-1.5"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><line x1="12" x2="12" y1="3" y2="21"></line></svg></button>
          <button class="p-1.5"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5"><path d="M2 12V2h10"></path><path d="M2 7h5"></path><path d="M22 12V2H12"></path><path d="M22 7h-5"></path><path d="M2 12v10h10"></path><path d="M2 17h5"></path><path d="M22 12v10H12"></path><path d="M22 17h-5"></path></svg></button>
          <button class="p-1.5">
            <div class="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
              <span class="text-xs">U</span>
            </div>
          </button>
        </div>
      </div>
    `,
    components: ["product-card", "navigation-bar"],
    inspired_by: "Instagram Mobile App"
  },
  {
    id: "ecommerce-app",
    name: "E-commerce Product Detail",
    description: "E-commerce product page with image gallery, product information, and purchase options",
    categories: ["ecommerce"],
    patterns: ["product", "checkout"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 600px; height: 300px;">
        <div class="border-b border-border p-3 flex justify-between items-center bg-card">
          <div class="flex gap-3 items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4"><path d="m15 18-6-6 6-6"></path></svg>
            <div class="font-bold text-sm">Product Detail</div>
          </div>
          <div class="flex gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4"><path d="m21 21-6-6m6 6v-4.8m0 4.8h-4.8"></path><path d="M3 16.2V21m0-4.8h4.8"></path><path d="M21 7.8V3m0 4.8h-4.8"></path><path d="M3 7.8V3m0 4.8H7.8"></path></svg>
            <div class="relative">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path><path d="M3 6h18"></path><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
              <div class="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full flex items-center justify-center">
                <span class="text-[8px] text-primary-foreground font-bold">3</span>
              </div>
            </div>
          </div>
        </div>
        <div class="flex-1 overflow-auto">
          <div class="grid grid-cols-2 gap-4 p-4">
            <div class="space-y-2">
              <div class="aspect-square bg-muted rounded-md flex items-center justify-center">
                <span class="text-xs text-muted-foreground">Main Product Image</span>
              </div>
              <div class="grid grid-cols-3 gap-2">
                <div class="aspect-square bg-muted/50 rounded-md"></div>
                <div class="aspect-square bg-muted/50 rounded-md"></div>
                <div class="aspect-square bg-muted/50 rounded-md"></div>
              </div>
            </div>
            <div>
              <h3 class="font-bold mb-1">Premium Wireless Headphones</h3>
              <div class="flex items-center gap-1 mb-2">
                <div class="flex text-amber-500">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3"><path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.006z" clip-rule="evenodd" /></svg>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3"><path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.006z" clip-rule="evenodd" /></svg>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3"><path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.006z" clip-rule="evenodd" /></svg>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3"><path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.006z" clip-rule="evenodd" /></svg>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3"><path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.006z" clip-rule="evenodd" /></svg>
                </div>
                <span class="text-xs text-muted-foreground">(128 reviews)</span>
              </div>
              <div class="flex items-baseline mb-3">
                <span class="text-lg font-bold mr-2">$199.99</span>
                <span class="text-xs line-through text-muted-foreground">$249.99</span>
                <span class="ml-2 bg-green-500/10 text-green-500 text-xs px-1.5 py-0.5 rounded">Save 20%</span>
              </div>
              <p class="text-xs text-muted-foreground mb-3">High-quality wireless headphones with noise cancellation, premium sound, and 30+ hour battery life.</p>
              <div class="mb-3">
                <div class="text-xs font-medium mb-1">Color</div>
                <div class="flex gap-2">
                  <div class="w-6 h-6 rounded-full bg-black border-2 border-primary"></div>
                  <div class="w-6 h-6 rounded-full bg-white border border-border"></div>
                  <div class="w-6 h-6 rounded-full bg-blue-600 border border-border"></div>
                </div>
              </div>
              <div class="flex items-center justify-between gap-2 mt-4">
                <div class="flex items-center border border-border rounded-md">
                  <button class="w-8 h-8 flex items-center justify-center text-sm">-</button>
                  <span class="w-8 text-center text-sm">1</span>
                  <button class="w-8 h-8 flex items-center justify-center text-sm">+</button>
                </div>
                <button class="flex-1 bg-primary text-primary-foreground text-xs font-medium rounded-md py-2">
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
    components: ["product-card", "grid-layout", "navigation-bar"],
    inspired_by: "Shopify Mobile App"
  },
  {
    id: "streaming-app",
    name: "Streaming Media App",
    description: "Modern streaming platform interface with featured content, categories, and content recommendations",
    categories: ["entertainment"],
    patterns: ["feed", "product"],
    complexity: "medium" as ComplexityLevel,
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-black" style="max-width: 600px; height: 300px;">
        <div class="p-3 flex justify-between items-center bg-black/80 border-b border-border/30">
          <div class="font-bold text-red-600 text-lg">STREAMFLIX</div>
          <div class="flex gap-4">
            <span class="text-gray-300 text-sm">TV Shows</span>
            <span class="text-gray-300 text-sm">Movies</span>
            <span class="text-gray-300 text-sm">My List</span>
          </div>
          <div class="w-7 h-7 rounded bg-gray-700 flex items-center justify-center">
            <span class="text-xs text-gray-300">U</span>
          </div>
        </div>
        <div class="flex-1 overflow-auto">
          <div class="relative h-36 w-full bg-gradient-to-b from-purple-900 to-gray-900 mb-4">
            <div class="absolute bottom-4 left-4">
              <h2 class="text-white font-bold mb-1">Cosmic Odyssey</h2>
              <div class="flex gap-2 mb-2">
                <span class="text-[10px] bg-red-600 text-white px-1 rounded">NEW</span>
                <span class="text-[10px] text-gray-300">2023</span>
                <span class="text-[10px] text-gray-300">TV-MA</span>
                <span class="text-[10px] text-gray-300">3 Seasons</span>
              </div>
              <div class="flex gap-2">
                <button class="bg-white text-black rounded text-xs px-3 py-1 font-medium flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3">
                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                  </svg>
                  Play
                </button>
                <button class="bg-gray-600/50 text-white rounded text-xs px-3 py-1 font-medium">+ My List</button>
              </div>
            </div>
          </div>
          
          <div class="px-4 mb-4">
            <h3 class="text-white text-sm font-medium mb-2">Trending Now</h3>
            <div class="flex gap-2 overflow-x-auto pb-2">
              <div class="h-24 w-40 flex-shrink-0 rounded bg-gray-800"></div>
              <div class="h-24 w-40 flex-shrink-0 rounded bg-gray-800"></div>
              <div class="h-24 w-40 flex-shrink-0 rounded bg-gray-800"></div>
              <div class="h-24 w-40 flex-shrink-0 rounded bg-gray-800"></div>
            </div>
          </div>
          
          <div class="px-4 mb-4">
            <h3 class="text-white text-sm font-medium mb-2">Continue Watching</h3>
            <div class="flex gap-2 overflow-x-auto pb-2">
              <div class="relative h-24 w-40 flex-shrink-0 rounded bg-gray-800">
                <div class="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
                  <div class="h-full w-3/4 bg-red-600"></div>
                </div>
              </div>
              <div class="relative h-24 w-40 flex-shrink-0 rounded bg-gray-800">
                <div class="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
                  <div class="h-full w-1/4 bg-red-600"></div>
                </div>
              </div>
              <div class="relative h-24 w-40 flex-shrink-0 rounded bg-gray-800">
                <div class="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
                  <div class="h-full w-1/2 bg-red-600"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
    components: ["grid-layout", "navigation-bar"],
    inspired_by: "Netflix"
  },
  {
    id: "music-app",
    name: "Music Streaming App",
    description: "Music player interface with album art, playback controls, and playlist management",
    categories: ["entertainment"],
    patterns: ["product", "feed"],
    complexity: "medium" as ComplexityLevel,
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-gradient-to-b from-emerald-950 to-black" style="max-width: 600px; height: 300px;">
        <div class="p-3 flex justify-between items-center border-b border-white/10">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-white"><path d="m15 18-6-6 6-6"></path></svg>
          <span class="text-white font-medium text-sm">Now Playing</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-white"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>
        </div>
        
        <div class="flex-1 flex flex-col items-center justify-center px-4 pt-4">
          <div class="w-40 h-40 bg-gray-800 rounded-md shadow-lg flex items-center justify-center mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-12 w-12 text-gray-600"><circle cx="12" cy="12" r="10"></circle><path d="M12 16.2a4.2 4.2 0 1 0 0-8.4 4.2 4.2 0 0 0 0 8.4Z"></path><circle cx="12" cy="12" r="1"></circle></svg>
          </div>
          
          <h2 class="text-white font-bold mb-1">Midnight Echoes</h2>
          <p class="text-gray-400 text-sm mb-6">Ethereal Dreams</p>
          
          <div class="w-full px-4 mb-4">
            <div class="w-full h-1 bg-gray-700 rounded-full mb-1">
              <div class="h-full w-1/3 bg-green-500 rounded-full"></div>
            </div>
            <div class="flex justify-between text-gray-400 text-xs">
              <span>1:28</span>
              <span>4:56</span>
            </div>
          </div>
          
          <div class="flex items-center justify-center gap-8 mb-8">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-gray-400"><polygon points="19 20 9 12 19 4 19 20"></polygon><line x1="5" x2="5" y1="19" y2="5"></line></svg>
            
            <div class="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-7 w-7 text-black"><path d="M5 3 19 12 5 21V3Z"></path></svg>
            </div>
            
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-gray-400"><polygon points="5 4 15 12 5 20 5 4"></polygon><line x1="19" x2="19" y1="5" y2="19"></line></svg>
          </div>
          
          <div class="flex gap-6 items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-gray-400"><path d="M9 17H7A5 5 0 0 1 7 7h2"></path><path d="M15 7h2a5 5 0 1 1 0 10h-2"></path><line x1="8" x2="16" y1="12" y2="12"></line></svg>
            
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-gray-400"><path d="M3 18v-6a9 9 0 0 1 18 0v6"></path><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path></svg>
            
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-gray-400"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path></svg>
            
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-gray-400"><path d="M12 3v12"></path><path d="m8 11 4 4 4-4"></path><path d="M8 5H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4"></path></svg>
          </div>
        </div>
      </div>
    `,
    components: ["product-card", "navigation-bar"],
    inspired_by: "Spotify Mobile App"
  },
  {
    id: "task-app",
    name: "Task Management App",
    description: "Modern task management interface with project boards, task lists, and productivity tracking",
    categories: ["productivity"],
    patterns: ["dashboard", "settings"],
    complexity: "medium" as ComplexityLevel,
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex overflow-hidden" style="max-width: 600px; height: 300px;">
        <!-- Sidebar -->
        <div class="w-1/5 bg-card border-r border-border p-3">
          <div class="mb-4">
            <div class="font-bold flex items-center gap-2 mb-3">
              <div class="w-5 h-5 rounded-md bg-indigo-500"></div>
              <span class="text-sm">TaskFlow</span>
            </div>
            <div class="w-full h-7 bg-background rounded-md flex items-center px-2 mb-5">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 text-muted-foreground mr-1"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>
              <span class="text-xs text-muted-foreground">Search</span>
            </div>
          </div>
          
          <div class="space-y-1 mb-5">
            <div class="px-2 py-1 rounded-md text-xs flex items-center gap-2 bg-primary/20 text-primary">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path></svg>
              Dashboard
            </div>
            <div class="px-2 py-1 rounded-md text-xs flex items-center gap-2 text-muted-foreground hover:bg-muted/50">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              Team
            </div>
            <div class="px-2 py-1 rounded-md text-xs flex items-center gap-2 text-muted-foreground hover:bg-muted/50">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M13 5v2"></path><path d="M13 17v2"></path><path d="M13 11v2"></path></svg>
              Calendar
            </div>
          </div>
          
          <div class="pt-3 border-t border-border">
            <div class="text-xs text-muted-foreground mb-2">Projects</div>
            <div class="space-y-1">
              <div class="px-2 py-1 rounded-md text-xs flex justify-between items-center text-foreground hover:bg-muted/50">
                <div class="flex items-center gap-2">
                  <div class="h-2 w-2 rounded-full bg-green-500"></div>
                  Marketing
                </div>
                <div class="text-[10px] bg-muted rounded-full px-1.5">3</div>
              </div>
              <div class="px-2 py-1 rounded-md text-xs flex justify-between items-center text-foreground hover:bg-muted/50">
                <div class="flex items-center gap-2">
                  <div class="h-2 w-2 rounded-full bg-blue-500"></div>
                  Website
                </div>
                <div class="text-[10px] bg-muted rounded-full px-1.5">7</div>
              </div>
              <div class="px-2 py-1 rounded-md text-xs flex justify-between items-center text-foreground hover:bg-muted/50">
                <div class="flex items-center gap-2">
                  <div class="h-2 w-2 rounded-full bg-amber-500"></div>
                  Product
                </div>
                <div class="text-[10px] bg-muted rounded-full px-1.5">5</div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Main content -->
        <div class="w-4/5 bg-background flex flex-col overflow-auto">
          <div class="py-3 px-4 border-b border-border flex justify-between items-center">
            <h1 class="font-bold">Dashboard</h1>
            <div class="flex items-center gap-3">
              <div class="bg-card px-2 py-1 rounded-md text-xs flex items-center gap-1 border border-border">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" x2="12" y1="15" y2="3"></line></svg>
                Share
              </div>
              <div class="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-medium">JD</div>
            </div>
          </div>
          
          <div class="p-4 grid grid-cols-3 gap-4">
            <div class="bg-card border border-border rounded-lg p-3">
              <div class="flex justify-between items-center mb-4">
                <div class="text-sm font-medium">Tasks</div>
                <span class="text-sm font-bold">24</span>
              </div>
              <div class="space-y-2 mb-3">
                <div class="w-full h-1.5 bg-muted-foreground/20 rounded-full">
                  <div class="h-full w-3/4 bg-primary rounded-full"></div>
                </div>
                <div class="flex justify-between text-xs text-muted-foreground">
                  <span>18 completed</span>
                  <span>75%</span>
                </div>
              </div>
            </div>
            
            <div class="bg-card border border-border rounded-lg p-3">
              <div class="flex justify-between items-center mb-4">
                <div class="text-sm font-medium">In Progress</div>
                <span class="text-sm font-bold">6</span>
              </div>
              <div class="space-y-2 mb-3">
                <div class="w-full h-1.5 bg-muted-foreground/20 rounded-full">
                  <div class="h-full w-1/4 bg-amber-500 rounded-full"></div>
                </div>
                <div class="flex justify-between text-xs text-muted-foreground">
                  <span>6 tasks</span>
                  <span>25%</span>
                </div>
              </div>
            </div>
            
            <div class="bg-card border border-border rounded-lg p-3">
              <div class="flex justify-between items-center mb-4">
                <div class="text-sm font-medium">Completed</div>
                <span class="text-sm font-bold">18</span>
              </div>
              <div class="space-y-2 mb-3">
                <div class="w-full h-1.5 bg-muted-foreground/20 rounded-full">
                  <div class="h-full w-3/4 bg-green-500 rounded-full"></div>
                </div>
                <div class="flex justify-between text-xs text-muted-foreground">
                  <span>18 tasks</span>
                  <span>75%</span>
                </div>
              </div>
            </div>
          </div>
          
          <div class="px-4 mb-4">
            <h2 class="font-medium text-sm mb-3">Recent Tasks</h2>
            <div class="bg-card border border-border rounded-lg divide-y divide-border">
              <div class="p-3 flex items-center gap-3">
                <input type="checkbox" class="h-4 w-4 rounded-sm border-muted-foreground" checked />
                <div class="flex-1">
                  <div class="text-sm line-through text-muted-foreground">Update homepage hero section</div>
                  <div class="text-xs text-muted-foreground">Website • Completed yesterday</div>
                </div>
                <div class="flex gap-1">
                  <div class="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px]">TS</div>
                  <div class="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px]">JD</div>
                </div>
              </div>
              <div class="p-3 flex items-center gap-3">
                <input type="checkbox" class="h-4 w-4 rounded-sm border-muted-foreground" />
                <div class="flex-1">
                  <div class="text-sm">Create social media campaign</div>
                  <div class="text-xs text-muted-foreground">Marketing • Due tomorrow</div>
                </div>
                <div class="flex gap-1">
                  <div class="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px]">MK</div>
                </div>
              </div>
              <div class="p-3 flex items-center gap-3">
                <input type="checkbox" class="h-4 w-4 rounded-sm border-muted-foreground" />
                <div class="flex-1">
                  <div class="text-sm">Finalize Q3 product roadmap</div>
                  <div class="text-xs text-muted-foreground">Product • Due in 3 days</div>
                </div>
                <div class="flex gap-1">
                  <div class="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px]">TS</div>
                  <div class="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px]">JD</div>
                  <div class="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px]">+2</div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    `,
    components: ["sidebar-layout", "grid-layout", "data-table"],
    inspired_by: "Notion and Linear"
  },
  {
    id: "stripe-signup",
    name: "Stripe Signup Flow",
    description: "Modern signup flow with step-by-step onboarding and verification",
    categories: ["auth"],
    patterns: ["signup", "onboarding"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background">
        <div class="p-3 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-3 w-3 text-primary"><path d="M12 2v20M2 12h20"/></svg>
            </div>
            <span class="font-medium text-xs">Create Account</span>
          </div>
          <div class="text-xs text-muted-foreground">Step 1 of 3</div>
        </div>
        <div class="p-4 flex-1 flex flex-col">
          <div class="space-y-2">
            <div>
              <label class="text-xs font-medium mb-1 block">Email</label>
              <input type="email" class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs" placeholder="you@example.com" />
            </div>
            <div>
              <label class="text-xs font-medium mb-1 block">Password</label>
              <input type="password" class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs" placeholder="••••••••" />
            </div>
            <div>
              <label class="text-xs font-medium mb-1 block">Confirm Password</label>
              <input type="password" class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs" placeholder="••••••••" />
            </div>
          </div>
          <div class="mt-auto pt-3">
            <button class="w-full bg-primary text-primary-foreground rounded-md py-1.5 text-xs font-medium">Continue</button>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Stripe"
  },
  {
    id: "github-login",
    name: "GitHub Authentication",
    description: "Clean authentication flow with social login options",
    categories: ["auth"],
    patterns: ["login"],
    complexity: "simple",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background">
        <div class="p-4 flex-1 flex flex-col items-center justify-center">
          <div class="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-5 w-5 text-primary"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M12 3v12"/><path d="M8 7l4 4 4-4"/></svg>
          </div>
          <h2 class="text-lg font-semibold mb-1">Sign in to GitHub</h2>
          <p class="text-xs text-muted-foreground mb-4">Enter your details below</p>
          <div class="w-full max-w-[240px] space-y-3">
            <div>
              <input type="text" class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs" placeholder="Username or email address" />
            </div>
            <div>
              <input type="password" class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs" placeholder="Password" />
            </div>
            <button class="w-full bg-primary text-primary-foreground rounded-md py-1.5 text-xs font-medium">Sign in</button>
            <div class="text-center">
              <a href="#" class="text-xs text-primary hover:underline">Forgot password?</a>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "GitHub"
  },
  {
    id: "linear-onboarding",
    name: "Linear Onboarding",
    description: "Interactive onboarding flow with team setup and preferences",
    categories: ["productivity"],
    patterns: ["onboarding"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background">
        <div class="p-3 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-3 w-3 text-primary"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8"/><path d="M15 11h-5"/><path d="M15 17h-5"/></svg>
            </div>
            <span class="font-medium text-xs">Set up your workspace</span>
          </div>
          <div class="text-xs text-muted-foreground">Step 2 of 4</div>
        </div>
        <div class="p-4 flex-1 flex flex-col">
          <div class="space-y-2">
            <div>
              <label class="text-xs font-medium mb-1 block">Workspace name</label>
              <input type="text" class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs" placeholder="Acme Inc." />
            </div>
            <div>
              <label class="text-xs font-medium mb-1 block">Team size</label>
              <select class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs">
                <option>1-10 people</option>
              </select>
            </div>
            <div>
              <label class="text-xs font-medium mb-1 block">Industry</label>
              <select class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs">
                <option>Technology</option>
              </select>
            </div>
          </div>
          <div class="mt-auto pt-3 flex justify-between">
            <button class="text-xs text-muted-foreground hover:text-foreground">Back</button>
            <button class="bg-primary text-primary-foreground rounded-md py-1.5 px-3 text-xs font-medium">Continue</button>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Linear"
  },
  {
    id: "stripe-dashboard",
    name: "Stripe Dashboard",
    description: "Modern dashboard with analytics and payment overview",
    categories: ["dashboard"],
    patterns: ["dashboard"],
    complexity: "complex",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 1200px; height: 300px;">
        <div class="p-4 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M12 2v20M2 12h20"/></svg>
            </div>
            <span class="font-medium">Dashboard</span>
          </div>
          <div class="flex items-center gap-2">
            <button class="text-sm text-muted-foreground hover:text-foreground">Today</button>
            <button class="text-sm text-muted-foreground hover:text-foreground">Week</button>
            <button class="text-sm text-muted-foreground hover:text-foreground">Month</button>
          </div>
        </div>
        <div class="p-6 flex-1 grid grid-cols-3 gap-4">
          <div class="bg-card rounded-lg p-4 border border-border">
            <div class="text-sm text-muted-foreground">Total Revenue</div>
            <div class="text-2xl font-semibold mt-1">$12,345</div>
            <div class="text-xs text-green-500 mt-1">+12.5% from last month</div>
          </div>
          <div class="bg-card rounded-lg p-4 border border-border">
            <div class="text-sm text-muted-foreground">Active Customers</div>
            <div class="text-2xl font-semibold mt-1">1,234</div>
            <div class="text-xs text-green-500 mt-1">+5.2% from last month</div>
          </div>
          <div class="bg-card rounded-lg p-4 border border-border">
            <div class="text-sm text-muted-foreground">Conversion Rate</div>
            <div class="text-2xl font-semibold mt-1">3.2%</div>
            <div class="text-xs text-red-500 mt-1">-0.5% from last month</div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Stripe"
  },
  {
    id: "shopify-checkout",
    name: "Shopify Checkout",
    description: "Streamlined checkout process with multiple payment options",
    categories: ["e-commerce"],
    patterns: ["checkout"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 800px; height: 300px;">
        <div class="p-4 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            </div>
            <span class="font-medium">Checkout</span>
          </div>
          <div class="text-xs text-muted-foreground">Step 2 of 3</div>
        </div>
        <div class="p-6 flex-1 flex">
          <div class="w-2/3 pr-6">
            <div class="space-y-4">
              <div>
                <label class="text-sm font-medium mb-1 block">Card number</label>
                <input type="text" class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="1234 5678 9012 3456" />
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="text-sm font-medium mb-1 block">Expiry date</label>
                  <input type="text" class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="MM/YY" />
                </div>
                <div>
                  <label class="text-sm font-medium mb-1 block">CVC</label>
                  <input type="text" class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="123" />
                </div>
              </div>
            </div>
          </div>
          <div class="w-1/3 pl-6 border-l border-border">
            <div class="text-sm font-medium mb-2">Order Summary</div>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-muted-foreground">Subtotal</span>
                <span>$99.99</span>
              </div>
              <div class="flex justify-between">
                <span class="text-muted-foreground">Shipping</span>
                <span>$5.00</span>
              </div>
              <div class="flex justify-between">
                <span class="text-muted-foreground">Tax</span>
                <span>$8.50</span>
              </div>
              <div class="border-t border-border pt-2 mt-2">
                <div class="flex justify-between font-medium">
                  <span>Total</span>
                  <span>$113.49</span>
                </div>
              </div>
            </div>
            <button class="w-full bg-primary text-primary-foreground rounded-md py-2 mt-4 text-sm font-medium">Pay $113.49</button>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Shopify"
  },
  {
    id: "dropbox-files",
    name: "Dropbox File Manager",
    description: "Modern file explorer with drag and drop support",
    categories: ["productivity"],
    patterns: ["file-management"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 1000px; height: 300px;">
        <div class="p-4 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>
            </div>
            <span class="font-medium">My Files</span>
          </div>
          <div class="flex items-center gap-2">
            <button class="text-sm text-muted-foreground hover:text-foreground">Upload</button>
            <button class="text-sm text-muted-foreground hover:text-foreground">New Folder</button>
          </div>
        </div>
        <div class="p-4 flex-1">
          <div class="grid grid-cols-4 gap-4">
            <div class="bg-card rounded-lg p-3 border border-border">
              <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>
              </div>
              <div class="text-sm font-medium">Documents</div>
              <div class="text-xs text-muted-foreground">24 files</div>
            </div>
            <div class="bg-card rounded-lg p-3 border border-border">
              <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>
              </div>
              <div class="text-sm font-medium">Images</div>
              <div class="text-xs text-muted-foreground">156 files</div>
            </div>
            <div class="bg-card rounded-lg p-3 border border-border">
              <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>
              </div>
              <div class="text-sm font-medium">Videos</div>
              <div class="text-xs text-muted-foreground">12 files</div>
            </div>
            <div class="bg-card rounded-lg p-3 border border-border">
              <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>
              </div>
              <div class="text-sm font-medium">Shared</div>
              <div class="text-xs text-muted-foreground">8 files</div>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Dropbox"
  },
  {
    id: "notion-search",
    name: "Notion Search",
    description: "Advanced search interface with filters and recent items",
    categories: ["productivity"],
    patterns: ["search"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 800px; height: 300px;">
        <div class="p-4 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
            </div>
            <input type="text" class="bg-transparent border-none focus:outline-none" placeholder="Search..." />
          </div>
          <div class="flex items-center gap-2">
            <button class="text-sm text-muted-foreground hover:text-foreground">Filter</button>
            <button class="text-sm text-muted-foreground hover:text-foreground">Sort</button>
          </div>
        </div>
        <div class="p-4 flex-1">
          <div class="space-y-4">
            <div class="flex items-center gap-2 p-2 hover:bg-muted rounded-md cursor-pointer">
              <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>
              </div>
              <div>
                <div class="text-sm font-medium">Project Proposal</div>
                <div class="text-xs text-muted-foreground">Updated 2 hours ago</div>
              </div>
            </div>
            <div class="flex items-center gap-2 p-2 hover:bg-muted rounded-md cursor-pointer">
              <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>
              </div>
              <div>
                <div class="text-sm font-medium">Meeting Notes</div>
                <div class="text-xs text-muted-foreground">Updated yesterday</div>
              </div>
            </div>
            <div class="flex items-center gap-2 p-2 hover:bg-muted rounded-md cursor-pointer">
              <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>
              </div>
              <div>
                <div class="text-sm font-medium">Task List</div>
                <div class="text-xs text-muted-foreground">Updated 3 days ago</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Notion"
  },
  {
    id: "calendly-booking",
    name: "Calendly Booking",
    description: "Streamlined appointment scheduling interface",
    categories: ["productivity"],
    patterns: ["booking"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 800px; height: 300px;">
        <div class="p-4 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
            </div>
            <span class="font-medium">Schedule a Meeting</span>
          </div>
          <div class="text-xs text-muted-foreground">30 min meeting</div>
        </div>
        <div class="p-6 flex-1">
          <div class="grid grid-cols-7 gap-2">
            <div class="text-center">
              <div class="text-xs text-muted-foreground">Mon</div>
              <div class="text-sm font-medium mt-1">15</div>
            </div>
            <div class="text-center">
              <div class="text-xs text-muted-foreground">Tue</div>
              <div class="text-sm font-medium mt-1">16</div>
            </div>
            <div class="text-center">
              <div class="text-xs text-muted-foreground">Wed</div>
              <div class="text-sm font-medium mt-1">17</div>
            </div>
            <div class="text-center">
              <div class="text-xs text-muted-foreground">Thu</div>
              <div class="text-sm font-medium mt-1">18</div>
            </div>
            <div class="text-center">
              <div class="text-xs text-muted-foreground">Fri</div>
              <div class="text-sm font-medium mt-1">19</div>
            </div>
            <div class="text-center">
              <div class="text-xs text-muted-foreground">Sat</div>
              <div class="text-sm font-medium mt-1">20</div>
            </div>
            <div class="text-center">
              <div class="text-xs text-muted-foreground">Sun</div>
              <div class="text-sm font-medium mt-1">21</div>
            </div>
          </div>
          <div class="mt-6 space-y-2">
            <div class="flex items-center justify-between p-2 hover:bg-muted rounded-md cursor-pointer">
              <div class="text-sm">9:00 AM</div>
              <div class="text-xs text-muted-foreground">Available</div>
            </div>
            <div class="flex items-center justify-between p-2 hover:bg-muted rounded-md cursor-pointer">
              <div class="text-sm">9:30 AM</div>
              <div class="text-xs text-muted-foreground">Available</div>
            </div>
            <div class="flex items-center justify-between p-2 hover:bg-muted rounded-md cursor-pointer">
              <div class="text-sm">10:00 AM</div>
              <div class="text-xs text-muted-foreground">Available</div>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Calendly"
  },
  {
    id: "intercom-help",
    name: "Intercom Help Center",
    description: "Modern help center with search and categories",
    categories: ["support"],
    patterns: ["help"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background">
        <div class="p-3 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-3 w-3 text-primary"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" x2="12.01" y1="17" y2="17"/></svg>
            </div>
            <span class="font-medium text-xs">Help Center</span>
          </div>
          <div class="flex items-center gap-2">
            <button class="text-xs text-muted-foreground hover:text-foreground">Contact Support</button>
          </div>
        </div>
        <div class="p-4 flex-1">
          <div class="relative">
            <input type="text" class="w-full rounded-md border border-input bg-background px-3 py-1.5 pl-7 text-xs" placeholder="Search for help..." />
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="absolute left-2 top-1.5 h-3 w-3 text-muted-foreground"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          </div>
          <div class="mt-4 grid grid-cols-2 gap-2">
            <div class="bg-card rounded-md p-2 border border-border">
              <div class="text-xs font-medium">Getting Started</div>
              <div class="text-[10px] text-muted-foreground mt-0.5">Learn the basics</div>
            </div>
            <div class="bg-card rounded-md p-2 border border-border">
              <div class="text-xs font-medium">Account Settings</div>
              <div class="text-[10px] text-muted-foreground mt-0.5">Manage account</div>
            </div>
            <div class="bg-card rounded-md p-2 border border-border">
              <div class="text-xs font-medium">Billing & Payments</div>
              <div class="text-[10px] text-muted-foreground mt-0.5">Subscription info</div>
            </div>
            <div class="bg-card rounded-md p-2 border border-border">
              <div class="text-xs font-medium">API Documentation</div>
              <div class="text-[10px] text-muted-foreground mt-0.5">Integration help</div>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Intercom"
  },
  {
    id: "github-code",
    name: "GitHub Code Review",
    description: "Modern code review interface with diff view",
    categories: ["developer-tools"],
    patterns: ["version-control"],
    complexity: "complex",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 1000px; height: 300px;">
        <div class="p-4 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M12 3v12"/><path d="M8 7l4 4 4-4"/></svg>
            </div>
            <span class="font-medium">Pull Request #123</span>
          </div>
          <div class="flex items-center gap-2">
            <button class="text-sm text-muted-foreground hover:text-foreground">Files changed</button>
            <button class="text-sm text-muted-foreground hover:text-foreground">Commits</button>
            <button class="text-sm text-muted-foreground hover:text-foreground">Checks</button>
          </div>
        </div>
        <div class="p-4 flex-1 overflow-y-auto">
          <div class="space-y-4">
            <div class="bg-card rounded-lg border border-border overflow-hidden">
              <div class="p-2 bg-muted text-sm font-medium">src/components/Button.tsx</div>
              <div class="p-4">
                <div class="flex items-start gap-4">
                  <div class="text-xs text-muted-foreground w-8">1</div>
                  <div class="text-sm">import React from 'react';</div>
                </div>
                <div class="flex items-start gap-4">
                  <div class="text-xs text-muted-foreground w-8">2</div>
                  <div class="text-sm">import { cn } from '@/lib/utils';</div>
                </div>
                <div class="flex items-start gap-4">
                  <div class="text-xs text-muted-foreground w-8">3</div>
                  <div class="text-sm bg-green-500/10">export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {</div>
                </div>
                <div class="flex items-start gap-4">
                  <div class="text-xs text-muted-foreground w-8">4</div>
                  <div class="text-sm bg-green-500/10">  variant?: 'primary' | 'secondary' | 'outline';</div>
                </div>
                <div class="flex items-start gap-4">
                  <div class="text-xs text-muted-foreground w-8">5</div>
                  <div class="text-sm bg-green-500/10">}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "GitHub"
  },
  {
    id: "vscode-interface",
    name: "VSCode Interface",
    description: "Modern code editor interface with file explorer and terminal",
    categories: ["Developer Tools"],
    patterns: ["Code Editor"],
    complexity: "complex",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex overflow-hidden bg-background" style="max-width: 800px; height: 300px;">
        <div class="w-1/5 bg-card border-r border-border flex flex-col">
          <div class="p-2 border-b border-border">
            <div class="text-xs font-medium">EXPLORER</div>
          </div>
          <div class="flex-1 overflow-y-auto p-2">
            <div class="text-xs mb-1 flex items-center justify-between">
              <span>PROJECT_NAME</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="M5 12h14"></path><path d="M12 5v14"></path></svg>
            </div>
            <div class="pl-4">
              <div class="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="M2 9V5c0-1.1.9-2 2-2h3.93a2 2 0 0 1 1.66.9l.82 1.2a2 2 0 0 0 1.66.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-1"></path><path d="M2 13h10"></path><path d="M9 16l-3-3 3-3"></path></svg>
                src
              </div>
              <div class="pl-4">
                <div class="text-xs flex items-center gap-1 text-purple-400">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="m13 2-2 2.5-2-2.5H5v3.5l-2-2-1 1 2 2.5-2 2.5 1 1 2-2V10h4l2-2.5 2 2.5h4V6.5l2 2 1-1-2-2.5 2-2.5-1-1-2 2V2h-4Z"></path><path d="M13 22v-5h4v-1.5l2 2 1-1-2-2.5 2-2.5-1-1-2 2V16h-4v-4h-2v4H7v-1.5l-2 2-1-1 2-2.5-2-2.5 1-1 2 2V14h4v5h2Z"></path></svg>
                  App.tsx
                </div>
                <div class="text-xs flex items-center gap-1 text-blue-400">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1"></path><path d="M16 3h1a2 2 0 0 1 2 2v5c0 1.1.9 2 2 2a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2h-1"></path></svg>
                  index.ts
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex-1 flex flex-col">
          <div class="bg-card border-b border-border flex text-xs">
            <div class="px-3 py-1.5 border-r border-border bg-background">App.tsx</div>
            <div class="px-3 py-1.5 border-r border-border">index.ts</div>
          </div>
          <div class="flex-1 bg-background p-4 overflow-auto">
            <div class="font-mono text-xs space-y-1">
              <div class="text-blue-400">import <span class="text-foreground">React</span> <span class="text-blue-400">from</span> <span class="text-green-400">'react'</span>;</div>
              <div class="text-blue-400">import <span class="text-foreground">{ Button }</span> <span class="text-blue-400">from</span> <span class="text-green-400">'./components/ui/button'</span>;</div>
              <div class="text-blue-400">import <span class="text-foreground">{ Input }</span> <span class="text-blue-400">from</span> <span class="text-green-400">'./components/ui/input'</span>;</div>
              <div></div>
              <div class="text-purple-400">function <span class="text-yellow-400">App</span>() {</div>
              <div class="pl-4">
                <div class="text-blue-400">return (</div>
                <div class="pl-4">
                  <div class="text-orange-400">&lt;<span class="text-foreground">div</span> <span class="text-green-400">className</span>=<span class="text-blue-400">"container"</span>&gt;</div>
                  <div class="pl-4">
                    <div class="text-orange-400">&lt;<span class="text-foreground">h1</span> <span class="text-green-400">className</span>=<span class="text-blue-400">"heading"</span>&gt;</div>
                    <div class="pl-4 text-foreground">Hello World</div>
                    <div class="text-orange-400">&lt;/<span class="text-foreground">h1</span>&gt;</div>
                    <div class="text-orange-400">&lt;<span class="text-foreground">Button</span>&gt;</div>
                    <div class="pl-4 text-foreground">Click me</div>
                    <div class="text-orange-400">&lt;/<span class="text-foreground">Button</span>&gt;</div>
                  </div>
                  <div class="text-orange-400">&lt;/<span class="text-foreground">div</span>&gt;</div>
                </div>
                <div class="text-blue-400">);</div>
              </div>
              <div class="text-purple-400">}</div>
              <div></div>
              <div class="text-blue-400">export <span class="text-blue-400">default</span> <span class="text-foreground">App</span>;</div>
            </div>
          </div>
          <div class="h-1/4 border-t border-border bg-card p-2">
            <div class="text-xs font-mono text-muted-foreground">
              <span class="text-green-400">$</span> npm run dev<br/>
              <span class="text-blue-400">vite v4.5.0</span> dev server running at:<br/>
              <span class="text-purple-400">➜</span> Local: <span class="text-blue-400 underline">http://localhost:3000/</span><br/>
              <span class="text-purple-400">➜</span> Network: use <span class="text-foreground">--host</span> to expose
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Visual Studio Code"
  },
  {
    id: "google-auth",
    name: "Google Authentication",
    description: "Clean authentication flow with social login options",
    categories: ["Authentication"],
    patterns: ["Login & Auth"],
    complexity: "simple",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background">
        <div class="p-4 flex-1 flex flex-col items-center justify-center">
          <div class="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-5 w-5 text-primary"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M12 3v12"/><path d="M8 7l4 4 4-4"/></svg>
          </div>
          <h2 class="text-lg font-semibold mb-1">Sign in to Google</h2>
          <p class="text-xs text-muted-foreground mb-4">Choose an account</p>
          <div class="w-full max-w-[240px] space-y-3">
            <button class="w-full flex items-center justify-center gap-2 border border-input rounded-md py-1.5 text-xs hover:bg-muted">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-3 w-3"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              <span>john.doe@gmail.com</span>
            </button>
            <button class="w-full flex items-center justify-center gap-2 border border-input rounded-md py-1.5 text-xs hover:bg-muted">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-3 w-3"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              <span>jane.smith@gmail.com</span>
            </button>
            <div class="text-center pt-1">
              <a href="#" class="text-xs text-primary hover:underline">Use another account</a>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Google"
  },
  {
    id: "microsoft-auth",
    name: "Microsoft Authentication",
    description: "Enterprise authentication with 2FA support",
    categories: ["Authentication"],
    patterns: ["Login & Auth"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 600px; height: 300px;">
        <div class="p-4 flex-1 flex flex-col items-center justify-center">
          <div class="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-5 w-5 text-primary"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M12 3v12"/><path d="M8 7l4 4 4-4"/></svg>
          </div>
          <h2 class="text-lg font-semibold mb-1">Sign in to Microsoft</h2>
          <p class="text-xs text-muted-foreground mb-4">Enter your work or school account</p>
          <div class="w-full max-w-[240px] space-y-3">
            <div>
              <input type="email" class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs" placeholder="someone@example.com" />
            </div>
            <div>
              <input type="password" class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs" placeholder="••••••••" />
            </div>
            <button class="w-full bg-primary text-primary-foreground rounded-md py-1.5 text-xs font-medium">Sign in</button>
            <div class="text-center">
              <a href="#" class="text-xs text-primary hover:underline">Forgot password?</a>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Microsoft"
  },
  {
    id: "salesforce-dashboard",
    name: "Salesforce Dashboard",
    description: "CRM dashboard with sales metrics and activity feed",
    categories: ["Dashboard"],
    patterns: ["Dashboard"],
    complexity: "complex",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 1200px; height: 300px;">
        <div class="p-4 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M12 2v20M2 12h20"/></svg>
            </div>
            <span class="font-medium">Sales Dashboard</span>
          </div>
          <div class="flex items-center gap-2">
            <button class="text-sm text-muted-foreground hover:text-foreground">Today</button>
            <button class="text-sm text-muted-foreground hover:text-foreground">Week</button>
            <button class="text-sm text-muted-foreground hover:text-foreground">Month</button>
          </div>
        </div>
        <div class="p-6 flex-1 grid grid-cols-3 gap-4">
          <div class="bg-card rounded-lg p-4 border border-border">
            <div class="text-sm text-muted-foreground">Total Revenue</div>
            <div class="text-2xl font-semibold mt-1">$45,678</div>
            <div class="text-xs text-green-500 mt-1">+15.2% from last month</div>
          </div>
          <div class="bg-card rounded-lg p-4 border border-border">
            <div class="text-sm text-muted-foreground">New Deals</div>
            <div class="text-2xl font-semibold mt-1">24</div>
            <div class="text-xs text-green-500 mt-1">+8.3% from last month</div>
          </div>
          <div class="bg-card rounded-lg p-4 border border-border">
            <div class="text-sm text-muted-foreground">Win Rate</div>
            <div class="text-2xl font-semibold mt-1">68%</div>
            <div class="text-xs text-red-500 mt-1">-2.1% from last month</div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Salesforce"
  },
  {
    id: "hubspot-dashboard",
    name: "HubSpot Dashboard",
    description: "Marketing dashboard with campaign performance",
    categories: ["Dashboard"],
    patterns: ["Dashboard"],
    complexity: "complex",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 1200px; height: 300px;">
        <div class="p-4 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M12 2v20M2 12h20"/></svg>
            </div>
            <span class="font-medium">Marketing Dashboard</span>
          </div>
          <div class="flex items-center gap-2">
            <button class="text-sm text-muted-foreground hover:text-foreground">Today</button>
            <button class="text-sm text-muted-foreground hover:text-foreground">Week</button>
            <button class="text-sm text-muted-foreground hover:text-foreground">Month</button>
          </div>
        </div>
        <div class="p-6 flex-1 grid grid-cols-3 gap-4">
          <div class="bg-card rounded-lg p-4 border border-border">
            <div class="text-sm text-muted-foreground">Website Visitors</div>
            <div class="text-2xl font-semibold mt-1">12,345</div>
            <div class="text-xs text-green-500 mt-1">+23.5% from last month</div>
          </div>
          <div class="bg-card rounded-lg p-4 border border-border">
            <div class="text-sm text-muted-foreground">Email Opens</div>
            <div class="text-2xl font-semibold mt-1">3,456</div>
            <div class="text-xs text-green-500 mt-1">+12.8% from last month</div>
          </div>
          <div class="bg-card rounded-lg p-4 border border-border">
            <div class="text-sm text-muted-foreground">Conversion Rate</div>
            <div class="text-2xl font-semibold mt-1">4.2%</div>
            <div class="text-xs text-red-500 mt-1">-0.8% from last month</div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "HubSpot"
  },
  {
    id: "amazon-product",
    name: "Amazon Product Page",
    description: "Modern product page with reviews and recommendations",
    categories: ["E-commerce"],
    patterns: ["Product Page"],
    complexity: "complex",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 1200px;">
        <div class="p-3 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-3 w-3 text-primary"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            </div>
            <span class="font-medium text-xs">Product Details</span>
          </div>
          <div class="flex items-center gap-2">
            <button class="text-xs text-muted-foreground hover:text-foreground">Share</button>
            <button class="text-xs text-muted-foreground hover:text-foreground">Save</button>
          </div>
        </div>
        <div class="p-3 grid grid-cols-2 gap-3">
          <div class="bg-card rounded-lg p-3 border border-border">
            <div class="aspect-square bg-muted rounded-lg"></div>
          </div>
          <div class="space-y-2">
            <div>
              <h1 class="text-sm font-semibold">Premium Wireless Headphones</h1>
              <div class="flex items-center gap-1 mt-1">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="none" class="h-3 w-3 text-yellow-500"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="none" class="h-3 w-3 text-yellow-500"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="none" class="h-3 w-3 text-yellow-500"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="none" class="h-3 w-3 text-yellow-500"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="none" class="h-3 w-3 text-yellow-500"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                </div>
                <span class="text-[10px] text-muted-foreground">(1,234 reviews)</span>
              </div>
            </div>
            <div class="text-base font-bold">$199.99</div>
            <div class="text-xs text-muted-foreground">In stock</div>
            <button class="w-full bg-primary text-primary-foreground rounded-md py-1.5 text-xs font-medium">Add to Cart</button>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Amazon"
  },
  {
    id: "etsy-cart",
    name: "Etsy Shopping Cart",
    description: "Modern shopping cart with item management",
    categories: ["E-commerce"],
    patterns: ["Shopping Cart"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 800px; height: 300px;">
        <div class="p-4 border-b border-border flex items-center justify-between bg-card">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="h-4 w-4 text-primary"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            </div>
            <span class="font-medium">Shopping Cart (3 items)</span>
          </div>
          <button class="text-sm text-muted-foreground hover:text-foreground">Clear Cart</button>
        </div>
        <div class="p-4 flex-1 space-y-4 overflow-y-auto">
          <div class="flex items-start gap-4 p-2 hover:bg-muted rounded-md cursor-pointer">
            <div class="w-16 h-16 bg-muted rounded-lg"></div>
            <div class="flex-1">
              <div class="text-sm font-medium">Handmade Ceramic Mug</div>
              <div class="text-xs text-muted-foreground">From: ArtisanPottery</div>
              <div class="flex items-center justify-between mt-2">
                <div class="text-sm font-medium">$24.99</div>
                <div class="flex items-center gap-2">
                  <button class="text-sm text-muted-foreground hover:text-foreground">-</button>
                  <span class="text-sm">1</span>
                  <button class="text-sm text-muted-foreground hover:text-foreground">+</button>
                </div>
              </div>
            </div>
          </div>
          <div class="flex items-start gap-4 p-2 hover:bg-muted rounded-md cursor-pointer">
            <div class="w-16 h-16 bg-muted rounded-lg"></div>
            <div class="flex-1">
              <div class="text-sm font-medium">Vintage Wall Clock</div>
              <div class="text-xs text-muted-foreground">From: RetroDecor</div>
              <div class="flex items-center justify-between mt-2">
                <div class="text-sm font-medium">$45.99</div>
                <div class="flex items-center gap-2">
                  <button class="text-sm text-muted-foreground hover:text-foreground">-</button>
                  <span class="text-sm">1</span>
                  <button class="text-sm text-muted-foreground hover:text-foreground">+</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="p-4 border-t border-border">
          <div class="flex justify-between text-sm mb-2">
            <span class="text-muted-foreground">Subtotal</span>
            <span class="font-medium">$70.98</span>
          </div>
          <div class="flex justify-between text-sm mb-2">
            <span class="text-muted-foreground">Shipping</span>
            <span class="font-medium">$5.99</span>
          </div>
          <div class="flex justify-between text-sm font-medium mt-4">
            <span>Total</span>
            <span>$76.97</span>
          </div>
          <button class="w-full bg-primary text-primary-foreground rounded-md py-2 mt-4 text-sm font-medium">Proceed to Checkout</button>
        </div>
      </div>
    `,
    inspired_by: "Etsy"
  },
  {
    id: "gitlab-pipeline",
    name: "GitLab CI/CD Pipeline",
    description: "CI/CD pipeline visualization with job status and logs",
    categories: ["Developer Tools"],
    patterns: ["CI/CD"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 800px; height: 300px;">
        <div class="p-3 border-b border-border bg-card flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="w-6 h-6 rounded bg-orange-500 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 text-white"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path></svg>
            </div>
            <div class="text-sm font-medium">Pipeline #12345</div>
            <div class="text-xs bg-green-500/10 text-green-500 rounded px-1.5 py-0.5">Passed</div>
          </div>
          <div class="text-xs text-muted-foreground">Triggered 2 hours ago by John Doe</div>
        </div>
        
        <div class="p-4 flex-1 overflow-auto">
          <div class="flex items-center gap-2 mb-6">
            <div class="text-xs px-2 py-1 rounded bg-primary/10 text-primary">main</div>
            <div class="text-xs text-muted-foreground">6fa3dc9</div>
            <div class="text-xs text-muted-foreground">Update README.md</div>
          </div>
          
          <div class="flex gap-4 relative mb-6">
            <div class="absolute top-4 left-3.5 bottom-4 w-0.5 bg-border"></div>
            
            <div class="flex flex-col items-center gap-1 z-10">
              <div class="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-white"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </div>
              <div class="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-white"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </div>
              <div class="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-white"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </div>
              <div class="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-white"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </div>
            </div>
            
            <div class="flex-1 flex flex-col gap-4">
              <div class="bg-card border border-border rounded-md p-3">
                <div class="flex justify-between items-center mb-2">
                  <div class="font-medium text-sm">Build</div>
                  <div class="text-xs text-green-500">Passed in 45s</div>
                </div>
                <div class="text-xs text-muted-foreground">Running npm install and build</div>
              </div>
              
              <div class="bg-card border border-border rounded-md p-3">
                <div class="flex justify-between items-center mb-2">
                  <div class="font-medium text-sm">Test</div>
                  <div class="text-xs text-green-500">Passed in 1m 23s</div>
                </div>
                <div class="text-xs text-muted-foreground">134 tests passed, 0 failures</div>
              </div>
              
              <div class="bg-card border border-border rounded-md p-3">
                <div class="flex justify-between items-center mb-2">
                  <div class="font-medium text-sm">Lint</div>
                  <div class="text-xs text-green-500">Passed in 12s</div>
                </div>
                <div class="text-xs text-muted-foreground">No linting errors found</div>
              </div>
              
              <div class="bg-card border border-border rounded-md p-3">
                <div class="flex justify-between items-center mb-2">
                  <div class="font-medium text-sm">Deploy</div>
                  <div class="text-xs text-green-500">Passed in 32s</div>
                </div>
                <div class="text-xs text-muted-foreground">Deployed to production environment</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "GitLab"
  },
  {
    id: "zendesk-support",
    name: "Zendesk Support Ticket",
    description: "Customer support ticket interface with conversation history",
    categories: ["Support"],
    patterns: ["Ticket Management"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 800px; height: 300px;">
        <div class="p-3 border-b border-border bg-card flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="w-6 h-6 rounded bg-blue-500 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 text-white"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
            </div>
            <div class="text-sm font-medium">Ticket #54321</div>
            <div class="text-xs bg-green-500/10 text-green-500 rounded px-1.5 py-0.5">Open</div>
          </div>
          <div class="text-xs text-muted-foreground">Updated 2 hours ago</div>
        </div>
        
        <div class="p-4 flex-1 overflow-auto">
          <div class="flex flex-col gap-4">
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 text-xs font-medium">JD</div>
              <div class="flex-1">
                <div class="bg-card border border-border rounded-lg p-3">
                  <div class="text-sm font-medium mb-1">John Doe</div>
                  <div class="text-xs text-muted-foreground mb-2">June 12, 2023 at 10:23 AM</div>
                  <p class="text-sm">I'm having trouble setting up the integration with my CRM system. The API key seems to be rejected even though I've verified it's correct. Can you help?</p>
                </div>
              </div>
            </div>
            
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-medium">ZD</div>
              <div class="flex-1">
                <div class="bg-primary/5 border border-primary/20 rounded-lg p-3">
                  <div class="text-sm font-medium mb-1">Sarah (Support Agent)</div>
                  <div class="text-xs text-muted-foreground mb-2">June 12, 2023 at 10:45 AM</div>
                  <p class="text-sm">Hi John, I'd be happy to help you with the integration issue. Could you please tell me which CRM you're trying to connect with and the specific error message you're receiving?</p>
                </div>
              </div>
            </div>
            
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 text-xs font-medium">JD</div>
              <div class="flex-1">
                <div class="bg-card border border-border rounded-lg p-3">
                  <div class="text-sm font-medium mb-1">John Doe</div>
                  <div class="text-xs text-muted-foreground mb-2">June 12, 2023 at 11:02 AM</div>
                  <p class="text-sm">I'm using Salesforce and the error message is "Authentication failed: Invalid API key or access token".</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="p-3 border-t border-border">
          <div class="flex gap-2">
            <input type="text" class="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm" placeholder="Type your reply..."/>
            <button class="bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-sm font-medium">Reply</button>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Zendesk"
  },
  {
    id: "linkedin-profile",
    name: "LinkedIn Profile Settings",
    description: "Professional profile settings page with customization options",
    categories: ["Productivity"],
    patterns: ["Profile & Settings"],
    complexity: "medium" as ComplexityLevel,
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 800px; height: 300px;">
        <div class="p-3 border-b border-border bg-card flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded bg-blue-600 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-white"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect width="4" height="12" x="2" y="9"></rect><circle cx="4" cy="4" r="2"></circle></svg>
            </div>
            <div class="text-base font-medium">Profile Settings</div>
          </div>
          <button class="text-sm bg-primary text-primary-foreground px-3 py-1 rounded">Save Changes</button>
        </div>
        
        <div class="flex flex-1 overflow-hidden">
          <div class="w-1/4 border-r border-border p-3 space-y-1">
            <div class="px-3 py-1.5 rounded-md bg-primary/10 text-primary text-sm font-medium">Profile</div>
            <div class="px-3 py-1.5 rounded-md hover:bg-muted/50 text-sm text-muted-foreground">Privacy</div>
            <div class="px-3 py-1.5 rounded-md hover:bg-muted/50 text-sm text-muted-foreground">Notifications</div>
            <div class="px-3 py-1.5 rounded-md hover:bg-muted/50 text-sm text-muted-foreground">Security</div>
            <div class="px-3 py-1.5 rounded-md hover:bg-muted/50 text-sm text-muted-foreground">Account</div>
            <div class="px-3 py-1.5 rounded-md hover:bg-muted/50 text-sm text-muted-foreground">Billing</div>
          </div>
          
          <div class="w-3/4 p-4 overflow-auto">
            <div class="flex items-start gap-4 mb-6">
              <div class="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 text-xl font-medium">JD</div>
              <div class="flex-1">
                <button class="text-xs bg-secondary text-secondary-foreground px-2 py-1 rounded">Change Photo</button>
                <p class="text-xs text-muted-foreground mt-1">Upload a photo of at least 400x400 pixels</p>
              </div>
            </div>
            
            <div class="space-y-4">
              <div>
                <label class="text-sm font-medium mb-1 block">Full Name</label>
                <input type="text" class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value="John Doe"/>
              </div>
              
              <div>
                <label class="text-sm font-medium mb-1 block">Headline</label>
                <input type="text" class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value="Senior Software Engineer at Tech Company"/>
              </div>
              
              <div>
                <label class="text-sm font-medium mb-1 block">Location</label>
                <input type="text" class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value="San Francisco, California, United States"/>
              </div>
              
              <div>
                <label class="text-sm font-medium mb-1 block">Industry</label>
                <select class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option>Computer Software</option>
                  <option>Information Technology</option>
                  <option>Financial Services</option>
                  <option>Marketing & Advertising</option>
                  <option>Internet</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "LinkedIn"
  },
  {
    id: "facebook-feed",
    name: "Facebook News Feed",
    description: "Social media news feed with posts, comments, and reactions",
    categories: ["Social"],
    patterns: ["Feed"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 600px; height: 300px;">
        <div class="p-3 border-b border-border bg-card flex justify-between items-center">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
              <span class="text-white font-bold text-xl">f</span>
            </div>
            <div class="relative">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>
              <input type="text" placeholder="Search Facebook" class="w-full rounded-full border border-input bg-background px-8 py-1 text-xs" />
            </div>
          </div>
          <div class="flex gap-3">
            <div class="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
              <span class="text-xs">JD</span>
            </div>
          </div>
        </div>
        <div class="flex-1 overflow-auto">
          <div class="p-4 border-b border-border">
            <div class="flex items-center gap-3 mb-3">
              <div class="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <span class="text-sm">TS</span>
              </div>
              <div>
                <div class="text-sm font-medium">Tech Solutions</div>
                <div class="text-xs text-muted-foreground">Yesterday at 4:30 PM</div>
              </div>
            </div>
            <div class="text-sm mb-3">
              Excited to announce our new product launch! After months of hard work, we're thrilled to introduce you to our latest innovation. Stay tuned for more details coming next week! #ProductLaunch #Innovation #TechSolutions
            </div>
            <div class="rounded-md bg-muted/50 aspect-video mb-3 flex items-center justify-center">
              <span class="text-xs text-muted-foreground">Product Launch Image</span>
            </div>
            <div class="flex items-center justify-between border-t border-border pt-3">
              <div class="flex items-center gap-1">
                <div class="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 text-white"><path d="M7 10v12"></path><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"></path></svg>
                </div>
                <span class="text-xs text-muted-foreground">126</span>
              </div>
              <div class="text-xs text-muted-foreground">42 comments • 15 shares</div>
            </div>
            <div class="flex items-center justify-between mt-3">
              <button class="flex-1 flex items-center justify-center gap-2 py-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-muted-foreground"><path d="M7 10v12"></path><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"></path></svg>
                <span class="text-xs text-muted-foreground">Like</span>
              </button>
              <button class="flex-1 flex items-center justify-center gap-2 py-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-muted-foreground"><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"></path></svg>
                <span class="text-xs text-muted-foreground">Comment</span>
              </button>
              <button class="flex-1 flex items-center justify-center gap-2 py-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-muted-foreground"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path><path d="M16 21h5v-5"></path></svg>
                <span class="text-xs text-muted-foreground">Share</span>
              </button>
            </div>
          </div>
          
          <div class="p-4 border-b border-border">
            <div class="flex items-center gap-3 mb-3">
              <div class="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <span class="text-sm">JD</span>
              </div>
              <div>
                <div class="text-sm font-medium">John Doe</div>
                <div class="text-xs text-muted-foreground">3 hours ago</div>
              </div>
            </div>
            <div class="text-sm mb-3">
              Just finished hiking at Grand Canyon! Incredible views and an amazing experience. Definitely recommend visiting if you haven't already. #Travel #GrandCanyon #Hiking
            </div>
            <div class="grid grid-cols-2 gap-1 mb-3">
              <div class="rounded-md bg-muted/50 aspect-video flex items-center justify-center">
                <span class="text-xs text-muted-foreground">Photo 1</span>
              </div>
              <div class="rounded-md bg-muted/50 aspect-video flex items-center justify-center">
                <span class="text-xs text-muted-foreground">Photo 2</span>
              </div>
              <div class="rounded-md bg-muted/50 aspect-video flex items-center justify-center">
                <span class="text-xs text-muted-foreground">Photo 3</span>
              </div>
              <div class="rounded-md bg-muted/50 aspect-video flex items-center justify-center">
                <span class="text-xs text-muted-foreground">Photo 4</span>
              </div>
            </div>
            <div class="flex items-center justify-between border-t border-border pt-3">
              <div class="flex items-center gap-1">
                <div class="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 text-white"><path d="M7 10v12"></path><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"></path></svg>
                </div>
                <span class="text-xs text-muted-foreground">83</span>
              </div>
              <div class="text-xs text-muted-foreground">17 comments • 4 shares</div>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Facebook"
  },
  {
    id: "whatsapp-chat",
    name: "WhatsApp Chat Interface",
    description: "Messaging interface with chat bubbles and media sharing",
    categories: ["Communication"],
    patterns: ["Chat & Messaging"],
    complexity: "medium",
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex overflow-hidden bg-gray-100" style="max-width: 600px; height: 300px;">
        <div class="w-1/3 bg-white border-r border-border flex flex-col">
          <div class="p-2 border-b border-border bg-gray-50 flex items-center justify-between">
            <div class="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
              <span class="text-xs">JD</span>
            </div>
            <div class="flex gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-gray-600"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-gray-600"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>
            </div>
          </div>
          <div class="p-2 border-b border-gray-200">
            <div class="relative">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>
              <input type="text" placeholder="Search or start new chat" class="w-full rounded-lg border border-gray-200 bg-gray-100 px-8 py-1.5 text-xs" />
            </div>
          </div>
          <div class="flex-1 overflow-auto">
            <div class="p-2 bg-gray-100 hover:bg-gray-200 cursor-pointer">
              <div class="flex gap-2">
                <div class="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <span class="text-xs text-white">TS</span>
                </div>
                <div class="flex-1">
                  <div class="flex justify-between">
                    <div class="font-medium text-sm">Team Sales</div>
                    <div class="text-xs text-gray-500">10:23 AM</div>
                  </div>
                  <div class="flex items-center justify-between">
                    <div class="text-xs text-gray-500 truncate w-36">Alice: The client meeting went well...</div>
                    <div class="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                      <span class="text-[10px] text-white">3</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="p-2 hover:bg-gray-200 cursor-pointer">
              <div class="flex gap-2">
                <div class="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <span class="text-xs text-white">MK</span>
                </div>
                <div class="flex-1">
                  <div class="flex justify-between">
                    <div class="font-medium text-sm">Marketing Team</div>
                    <div class="text-xs text-gray-500">Yesterday</div>
                  </div>
                  <div class="text-xs text-gray-500 truncate">You: I'll send the report later today</div>
                </div>
              </div>
            </div>
            <div class="p-2 hover:bg-gray-200 cursor-pointer">
              <div class="flex gap-2">
                <div class="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                  <span class="text-xs text-white">JS</span>
                </div>
                <div class="flex-1">
                  <div class="flex justify-between">
                    <div class="font-medium text-sm">John Smith</div>
                    <div class="text-xs text-gray-500">Wednesday</div>
                  </div>
                  <div class="text-xs text-gray-500 truncate">Let's meet for coffee next week</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex-1 flex flex-col bg-[#e4ddd8]">
          <div class="p-2 border-b border-border bg-gray-50 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <div class="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center">
                <span class="text-xs text-white">TS</span>
              </div>
              <div>
                <div class="font-medium text-sm">Team Sales</div>
                <div class="text-xs text-gray-500">Alice, Bob, Charlie, +5 more</div>
              </div>
            </div>
            <div class="flex gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-gray-600"><path d="M23 7 6.68 20.85a2.54 2.54 0 0 1-4.2-2.8L13.15 2.94a1.7 1.7 0 0 1 2.43-.46l6.89 6.09a1.7 1.7 0 0 1 .46 2.43Z"></path><path d="M19 2v6h6"></path></svg>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-gray-600"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>
            </div>
          </div>
          <div class="flex-1 overflow-auto p-3 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTAwIDJhOTggOTggMCAwMC00MS4wNyAxODcuNTZjLTE3Ljk4IDUuODctMzguNiAxNC4zLTUxLjY2IDI0Ljc3IDE5LjQ3LTQuNjkgNDAuODItNy41MSA2Mi45Mi0xNC41NEE5OCA5OCAwIDAwMTAwIDJ6IiBmaWxsPSJyZ2JhKDAsMCwwLDAuMDIpIi8+PC9zdmc+')]">
            <div class="text-xs text-center text-gray-500 mb-3">Today</div>
            
            <div class="flex justify-start mb-3">
              <div class="bg-white rounded-lg p-2 max-w-[70%] shadow-sm">
                <div class="text-xs">
                  <span class="text-green-600 font-medium">Alice</span>: The client meeting went well. They're interested in our new product line!
                </div>
                <div class="text-[10px] text-gray-500 text-right">10:12 AM</div>
              </div>
            </div>
            
            <div class="flex justify-start mb-3">
              <div class="bg-white rounded-lg p-2 max-w-[70%] shadow-sm">
                <div class="text-xs">
                  <span class="text-blue-600 font-medium">Bob</span>: That's great news! What products were they most interested in?
                </div>
                <div class="text-[10px] text-gray-500 text-right">10:15 AM</div>
              </div>
            </div>
            
            <div class="flex justify-start mb-3">
              <div class="bg-white rounded-lg p-2 max-w-[70%] shadow-sm">
                <div class="text-xs">
                  <span class="text-green-600 font-medium">Alice</span>: Mostly the premium tier. I've prepared a proposal document to share with everyone.
                </div>
                <div class="flex items-center mt-2 bg-gray-100 rounded p-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-red-500 mr-2"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                  <div class="text-xs">Client_Proposal.pdf</div>
                </div>
                <div class="text-[10px] text-gray-500 text-right">10:20 AM</div>
              </div>
            </div>
            
            <div class="flex justify-end mb-3">
              <div class="bg-green-100 rounded-lg p-2 max-w-[70%] shadow-sm">
                <div class="text-xs">Thanks for the update! I'll review the proposal and provide feedback.</div>
                <div class="text-[10px] text-gray-500 text-right">10:23 AM ✓✓</div>
              </div>
            </div>
          </div>
          <div class="p-2 bg-gray-50 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-gray-600"><circle cx="12" cy="12" r="10"></circle><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><line x1="9" x2="9.01" y1="9" y2="9"></line><line x1="15" x2="15.01" y1="9" y2="9"></line></svg>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-gray-600"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
            <input type="text" placeholder="Type a message" class="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs" />
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-gray-600"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></svg>
            <div class="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-white"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" x2="12" y1="19" y2="22"></line></svg>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "WhatsApp"
  },
  {
    id: "slack-notifications",
    name: "Slack Notifications Center",
    description: "Centralized notifications panel with message types and timestamps",
    categories: ["Productivity", "Communication"],
    patterns: ["Notifications"],
    complexity: "medium" as ComplexityLevel,
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 600px; height: 300px;">
        <div class="p-3 border-b border-border bg-card flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded bg-purple-600 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-white"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path><path d="m9 12 2 2 4-4"></path></svg>
            </div>
            <div class="text-base font-medium">Notifications</div>
          </div>
          <div class="flex items-center gap-2">
            <button class="text-xs bg-secondary px-2 py-1 rounded">Mark all as read</button>
            <button class="text-xs bg-secondary/50 hover:bg-secondary px-2 py-1 rounded">Settings</button>
          </div>
        </div>
        
        <div class="p-1 flex bg-muted/30 border-b border-border">
          <button class="flex-1 py-2 text-center text-sm text-primary border-b-2 border-primary font-medium">All</button>
          <button class="flex-1 py-2 text-center text-sm text-muted-foreground hover:text-foreground">Mentions</button>
          <button class="flex-1 py-2 text-center text-sm text-muted-foreground hover:text-foreground">Reactions</button>
        </div>
        
        <div class="flex-1 overflow-auto">
          <div class="p-3 border-b border-border hover:bg-muted/30 cursor-pointer">
            <div class="flex gap-3">
              <div class="w-9 h-9 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 text-xs font-medium">AM</div>
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <div class="font-medium text-sm">Alex Miller</div>
                  <div class="text-xs text-muted-foreground">10:45 AM</div>
                </div>
                <div class="text-sm text-muted-foreground mb-1">Mentioned you in #project-alpha</div>
                <div class="text-xs bg-muted/50 p-2 rounded border border-border">"@user Could you review the latest design when you get a chance?"</div>
              </div>
            </div>
          </div>
          
          <div class="p-3 border-b border-border hover:bg-muted/30 cursor-pointer bg-primary/5">
            <div class="flex gap-3">
              <div class="w-9 h-9 rounded bg-blue-600/90 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-white"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
              </div>
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <div class="font-medium text-sm">Channel Updates</div>
                  <div class="text-xs text-muted-foreground">Yesterday</div>
                </div>
                <div class="text-sm text-muted-foreground mb-1">New channel created</div>
                <div class="text-xs bg-muted/50 p-2 rounded border border-border">Sarah created #marketing-2023</div>
              </div>
            </div>
          </div>
          
          <div class="p-3 border-b border-border hover:bg-muted/30 cursor-pointer">
            <div class="flex gap-3">
              <div class="w-9 h-9 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-500 text-xs font-medium">JT</div>
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <div class="font-medium text-sm">Jamie Thompson</div>
                  <div class="text-xs text-muted-foreground">Yesterday</div>
                </div>
                <div class="text-sm text-muted-foreground mb-1">Reacted to your message</div>
                <div class="flex items-center gap-1 text-xs">
                  <div class="bg-muted/50 p-1 rounded border border-border">👍</div>
                  <span>on your message in #general</span>
                </div>
              </div>
            </div>
          </div>
          
          <div class="p-3 border-b border-border hover:bg-muted/30 cursor-pointer">
            <div class="flex gap-3">
              <div class="w-9 h-9 rounded bg-purple-600/90 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-white"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><path d="M12 11h4"></path><path d="M12 16h4"></path><path d="M8 11h.01"></path><path d="M8 16h.01"></path></svg>
              </div>
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <div class="font-medium text-sm">Reminder</div>
                  <div class="text-xs text-muted-foreground">2 days ago</div>
                </div>
                <div class="text-sm text-muted-foreground">Weekly team meeting starting in 15 minutes</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Slack"
  },
  {
    id: "openai-assistant",
    name: "OpenAI Chat Interface",
    description: "AI-powered chat interface with conversation history",
    categories: ["Productivity", "Developer Tools"],
    patterns: ["AI Features"],
    complexity: "complex" as ComplexityLevel,
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 800px; height: 300px;">
        <div class="p-3 border-b border-border bg-card flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded bg-emerald-600 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-white"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path><path d="M16 16h5v5"></path></svg>
            </div>
            <div class="text-base font-medium">AI Assistant</div>
          </div>
          <div class="flex items-center gap-2">
            <button class="h-7 w-7 rounded-full flex items-center justify-center hover:bg-muted">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" x2="21" y1="14" y2="3"></line></svg>
            </button>
            <button class="h-7 w-7 rounded-full flex items-center justify-center hover:bg-muted">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>
            </button>
          </div>
        </div>
        
        <div class="flex-1 overflow-auto p-4 space-y-4">
          <div class="flex gap-3">
            <div class="w-8 h-8 rounded-full bg-card border border-emerald-500 flex items-center justify-center flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-emerald-500"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path><path d="M16 16h5v5"></path></svg>
            </div>
            <div class="bg-card border border-border rounded-lg p-3 text-sm max-w-[85%]">
              <p>Hello! I'm your AI assistant. How can I help you today?</p>
            </div>
          </div>
          
          <div class="flex gap-3 justify-end">
            <div class="bg-primary/10 rounded-lg p-3 text-sm max-w-[85%]">
              <p>Can you explain how to implement a binary search algorithm in Python?</p>
            </div>
            <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-primary text-xs font-medium">
              You
            </div>
          </div>
          
          <div class="flex gap-3">
            <div class="w-8 h-8 rounded-full bg-card border border-emerald-500 flex items-center justify-center flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-emerald-500"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path><path d="M16 16h5v5"></path></svg>
            </div>
            <div class="bg-card border border-border rounded-lg p-3 text-sm max-w-[85%]">
              <p>Here's a Python implementation of binary search:</p>
              <pre class="bg-muted/50 p-2 rounded mt-2 text-xs overflow-x-auto">
def binary_search(arr, target):
    left, right = 0, len(arr) - 1
    
    while left <= right:
        mid = (left + right) // 2
        
        if arr[mid] == target:
            return mid
        elif arr[mid] < target:
            left = mid + 1
        else:
            right = mid - 1
            
    return -1  # Target not found

# Example usage
sorted_array = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
result = binary_search(sorted_array, 7)
print(f"Found at index: {result}")
              </pre>
              <p class="mt-2">This algorithm works by repeatedly dividing the search interval in half. It requires the array to be sorted and has a time complexity of O(log n).</p>
            </div>
          </div>
        </div>
        
        <div class="p-3 border-t border-border">
          <div class="flex gap-2">
            <input type="text" class="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm" placeholder="Ask me anything..."/>
            <button class="bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-sm font-medium">Send</button>
          </div>
        </div>
      </div>
    `,
    inspired_by: "OpenAI ChatGPT"
  },
  {
    id: "google-search",
    name: "Google Search & Filter",
    description: "Modern search interface with filters and suggestions",
    categories: ["Productivity"],
    patterns: ["Search & Filter"],
    complexity: "medium" as ComplexityLevel,
    preview: `
      <div class="w-full h-full border border-border rounded-lg flex flex-col overflow-hidden bg-background" style="max-width: 800px; height: 300px;">
        <div class="p-3 border-b border-border bg-card flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
            </div>
            <div class="text-base font-medium">Search</div>
          </div>
          <div class="flex items-center gap-2">
            <button class="h-7 w-7 rounded-full flex items-center justify-center hover:bg-muted">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
            </button>
            <button class="h-7 w-7 rounded-full flex items-center justify-center hover:bg-muted">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
            </button>
          </div>
        </div>
        
        <div class="p-4 flex-1 overflow-auto">
          <div class="w-full max-w-xl mx-auto">
            <div class="relative mb-6">
              <input type="text" class="w-full rounded-full border border-input bg-background px-4 py-2.5 pr-10 text-sm" value="react component library" />
              <button class="absolute right-3 top-1/2 transform -translate-y-1/2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 text-primary"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
              </button>
            </div>
            
            <div class="flex gap-2 mb-4 overflow-x-auto pb-2">
              <button class="px-3 py-1 rounded-full text-xs bg-primary/10 text-primary whitespace-nowrap">All</button>
              <button class="px-3 py-1 rounded-full text-xs bg-secondary whitespace-nowrap">Images</button>
              <button class="px-3 py-1 rounded-full text-xs bg-secondary whitespace-nowrap">Videos</button>
              <button class="px-3 py-1 rounded-full text-xs bg-secondary whitespace-nowrap">News</button>
              <button class="px-3 py-1 rounded-full text-xs bg-secondary whitespace-nowrap">Shopping</button>
              <button class="px-3 py-1 rounded-full text-xs bg-secondary whitespace-nowrap">Books</button>
              <button class="px-3 py-1 rounded-full text-xs bg-secondary whitespace-nowrap">More</button>
            </div>
            
            <div class="flex gap-2 mb-4 overflow-x-auto pb-2">
              <span class="text-xs text-muted-foreground">Filters:</span>
              <button class="px-2 py-0.5 text-xs bg-secondary/50 hover:bg-secondary rounded flex items-center gap-1">
                Open Source 
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ml-1 h-3 w-3"><path d="m6 9 6 6 6-6"/></svg>
              </button>
              <button class="px-2 py-0.5 text-xs bg-secondary/50 hover:bg-secondary rounded flex items-center gap-1">
                Last Year
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ml-1 h-3 w-3"><path d="m6 9 6 6 6-6"/></svg>
              </button>
              <button class="px-2 py-0.5 text-xs bg-secondary/50 hover:bg-secondary rounded flex items-center gap-1">
                React
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ml-1 h-3 w-3"><path d="m6 9 6 6 6-6"/></svg>
              </button>
            </div>
            
            <div class="text-xs text-muted-foreground mb-4">About 1,250,000 results (0.35 seconds)</div>
            
            <div class="space-y-4">
              <div class="border-b border-border pb-3">
                <div class="flex flex-col">
                  <a href="#" class="text-primary hover:underline font-medium mb-1">shadcn/ui - The React Component Library You Always Wanted</a>
                  <div class="text-xs text-green-600 mb-1">https://ui.shadcn.com</div>
                  <p class="text-sm text-muted-foreground">A collection of beautifully designed components that you can copy and paste into your apps. Built with Radix UI and Tailwind CSS.</p>
                </div>
              </div>
              
              <div class="border-b border-border pb-3">
                <div class="flex flex-col">
                  <a href="#" class="text-primary hover:underline font-medium mb-1">MUI - The React UI Library You've Always Wanted</a>
                  <div class="text-xs text-green-600 mb-1">https://mui.com</div>
                  <p class="text-sm text-muted-foreground">MUI offers a comprehensive suite of free UI tools to help you ship new features faster. Start with Material UI, our fully-loaded component library.</p>
                </div>
              </div>
              
              <div class="border-b border-border pb-3">
                <div class="flex flex-col">
                  <a href="#" class="text-primary hover:underline font-medium mb-1">Chakra UI - Simple, Modular Components for React</a>
                  <div class="text-xs text-green-600 mb-1">https://chakra-ui.com</div>
                  <p class="text-sm text-muted-foreground">Chakra UI is a simple, modular and accessible component library that gives you the building blocks to build your React applications.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
    inspired_by: "Google Search"
  }
];

// Add these constants for App UI categorization
const APP_CATEGORIES = [
  "All Apps",
  "Authentication",
  "Dashboard",
  "E-commerce",
  "Productivity",
  "Communication",
  "Social",
  "Support",
  "Developer Tools"
] as const;

const UI_PATTERNS = [
  "Signup Flow",
  "Login & Auth",
  "Onboarding",
  "Dashboard",
  "Checkout",
  "File Management",
  "Search & Filter",
  "Profile & Settings",
  "Notifications",
  "Chat & Messaging",
  "Booking",
  "Help & Support",
  "AI Features",
  "Version Control"
] as const;

const MAX_VISIBLE_COMPONENTS = 8;
const MAX_VISIBLE_TAGS = 5;
const MAX_VISIBLE_APP_TEMPLATES = 6;

// Add this near the top of the file where other constants are defined
const appCategories = [
  {
    id: "all",
    name: "All Apps",
    icon: <Box size={14} />
  }, 
  {
    id: "Authentication",
    name: "Authentication",
    icon: <Lock size={14} />
  }, 
  {
    id: "Dashboard",
    name: "Dashboard",
    icon: <BarChart size={14} />
  }, 
  {
    id: "E-commerce",
    name: "E-commerce",
    icon: <ShoppingCart size={14} />
  }, 
  {
    id: "Productivity",
    name: "Productivity",
    icon: <Zap size={14} />
  },
  {
    id: "Communication",
    name: "Communication",
    icon: <MessageSquare size={14} />
  },
  {
    id: "Social",
    name: "Social",
    icon: <Users size={14} />
  },
  {
    id: "Support",
    name: "Support",
    icon: <Wrench size={14} />
  },
  {
    id: "Developer Tools",
    name: "Developer Tools",
    icon: <Terminal size={14} />
  }
];

// Reusable CategoryButton component
const CategoryButton = ({ 
  category, 
  isActive, 
  onClick, 
  children 
}: { 
  category?: { id: string, name: string, icon?: React.ReactNode }, 
  isActive: boolean, 
  onClick: () => void, 
  children?: React.ReactNode 
}) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-full transition-all duration-200 h-8 ${
        isActive 
          ? "bg-primary text-primary-foreground shadow-sm shadow-primary/20" 
          : "bg-black/50 text-foreground hover:bg-primary/10 border border-white/5"
      }`}
    >
      {category?.icon && <span className="opacity-80">{category.icon}</span>}
      {category?.name || children}
    </button>
  );
};

// Custom Badge component that doesn't include the data-lov attributes
const CategoryBadge = ({ children, className }: { children: React.ReactNode, className?: string }) => {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${className || ''}`}>
      {children}
    </span>
  );
};

// Helper function to generate complete HTML with styles for copying
const generateCompleteHtml = (template: AppUiTemplate | ComponentItem): string => {
  const isAppTemplate = 'categories' in template;
  const content = template.preview;
  
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${template.name}</title>
  <style>
    /* Reset and base styles */
    *, *::before, *::after {
      box-sizing: border-box;
    }
    
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      line-height: 1.5;
      background-color: #1a1a1a;
      color: #fafafa;
    }
    
    /* Utility classes */
    .flex { display: flex; }
    .items-center { align-items: center; }
    .justify-center { justify-content: center; }
    .justify-between { justify-content: space-between; }
    .flex-col { flex-direction: column; }
    .w-full { width: 100%; }
    .h-full { height: 100%; }
    .mx-auto { margin-left: auto; margin-right: auto; }
    .rounded-lg { border-radius: 0.5rem; }
    .rounded-md { border-radius: 0.375rem; }
    .p-4 { padding: 1rem; }
    .p-3 { padding: 0.75rem; }
    .p-2 { padding: 0.5rem; }
    .p-1 { padding: 0.25rem; }
    .px-4 { padding-left: 1rem; padding-right: 1rem; }
    .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
    .gap-2 { gap: 0.5rem; }
    .space-y-2 > * + * { margin-top: 0.5rem; }
    .font-medium { font-weight: 500; }
    .font-bold { font-weight: 700; }
    .text-sm { font-size: 0.875rem; }
    .text-xs { font-size: 0.75rem; }
    .border { border: 1px solid; }
    .mb-4 { margin-bottom: 1rem; }
    .mt-4 { margin-top: 1rem; }
    
    /* Theme variables */
    :root {
      --background: #1a1a1a;
      --foreground: #fafafa;
      --card: #242424;
      --card-foreground: #fafafa;
      --primary: #6366f1;
      --primary-foreground: #fafafa;
      --secondary: #3f3f46;
      --muted: #3f3f46;
      --muted-foreground: #a1a1aa;
      --accent: #3f3f46;
      --border: #2e2e2e;
    }
    
    /* Component styles */
    .bg-background { background-color: var(--background); }
    .bg-card { background-color: var(--card); }
    .bg-primary { background-color: var(--primary); }
    .bg-primary\/20 { background-color: rgba(99, 102, 241, 0.2); }
    .bg-primary\/10 { background-color: rgba(99, 102, 241, 0.1); }
    .bg-muted { background-color: var(--muted); }
    .bg-muted\/50 { background-color: rgba(63, 63, 70, 0.5); }
    .text-foreground { color: var(--foreground); }
    .text-muted-foreground { color: var(--muted-foreground); }
    .text-primary { color: var(--primary); }
    .text-primary-foreground { color: var(--primary-foreground); }
    .border-border { border-color: var(--border); }
    .border-primary { border-color: var(--primary); }
    .border-primary\/30 { border-color: rgba(99, 102, 241, 0.3); }
    
    /* Button styles */
    button, .button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.375rem;
      font-weight: 500;
      outline: none;
      transition: background-color 0.2s, color 0.2s, border-color 0.2s;
      cursor: pointer;
    }
    
    button.bg-primary, .button.bg-primary {
      background-color: var(--primary);
      color: var(--primary-foreground);
    }
    
    button:hover.bg-primary, .button:hover.bg-primary {
      background-color: rgba(99, 102, 241, 0.9);
    }
    
    button.outline, .button.outline {
      background-color: transparent;
      border: 1px solid var(--border);
      color: var(--foreground);
    }
    
    button:hover.outline, .button:hover.outline {
      background-color: rgba(63, 63, 70, 0.1);
    }
    
    /* Input styles */
    input, textarea, select {
      background-color: var(--background);
      border: 1px solid var(--border);
      border-radius: 0.375rem;
      color: var(--foreground);
      outline: none;
      transition: border-color 0.2s;
    }
    
    input:focus, textarea:focus, select:focus {
      border-color: var(--primary);
    }
    
    /* Additional styles for specific templates */
    ${isAppTemplate ? `
    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 1rem;
    }
    
    /* Additional styles for app UI templates */
    .shadow-sm { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
    .grid { display: grid; }
    .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .divide-y > * + * { border-top-width: 1px; border-top-style: solid; }
    .divide-border > * + * { border-color: var(--border); }
    ` : `
    /* Additional styles for component templates */
    `}
  </style>
</head>
<body class="bg-background">
  <div class="p-4 flex items-center justify-center min-h-screen">
    ${content}
  </div>
</body>
</html>`;
};

const ComponentLibrary = () => {
  const { user } = useAuth();
  const { hasUsedTool, markToolAsUsed } = useGuestUsage();
  const [copied, setCopied] = useState<string | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<ComponentItem | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [animateCards, setAnimateCards] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);
  const [complexityFilter, setComplexityFilter] = useState<ComplexityLevel | 'all'>('all');
  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [activeTab, setActiveTab] = useState<'components' | 'appUIs'>('components');
  const [showAllComponents, setShowAllComponents] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);
  const [showComponentDetails, setShowComponentDetails] = useState(false);
  
  // New state variables for App UIs filtering
  const [activeAppCategory, setActiveAppCategory] = useState("all");
  const [activePattern, setActivePattern] = useState("all");
  const [appSearchQuery, setAppSearchQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<AppUiTemplate | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showAllAppPatterns, setShowAllAppPatterns] = useState(false);
  const [appViewMode, setAppViewMode] = useState<ViewMode>("grid");
  // Add state variable for visible app categories
  const [showAllAppCategories, setShowAllAppCategories] = useState(false);
  
  useEffect(() => {
    setAnimateCards(true);
    const timer = setTimeout(() => setAnimateCards(false), 500);
    return () => clearTimeout(timer);
  }, [activeCategory, complexityFilter, activeTagFilter, showFavoritesOnly]);
  
  useEffect(() => {
    // Load favorites from localStorage if user is logged in
    if (user) {
      const savedFavorites = localStorage.getItem(`cl_favorites_${user.id}`);
      if (savedFavorites) {
        setFavorites(JSON.parse(savedFavorites));
      }
    }
  }, [user]);

  useEffect(() => {
    // Reset showAllComponents when category changes
    setShowAllComponents(false);
  }, [activeCategory, complexityFilter, activeTagFilter, showFavoritesOnly, searchQuery]);

  const saveFavorites = (newFavorites: string[]) => {
    if (user) {
      localStorage.setItem(`cl_favorites_${user.id}`, JSON.stringify(newFavorites));
      setFavorites(newFavorites);
    }
  };

  const toggleFavorite = (componentId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    
    if (!user) {
      setIsLoginPromptOpen(true);
      return;
    }
    
    const newFavorites = favorites.includes(componentId)
      ? favorites.filter(id => id !== componentId)
      : [...favorites, componentId];
    
    saveFavorites(newFavorites);
  };
  
  // Get all unique tags from components
  const allTags = Array.from(new Set(components.flatMap(component => component.tags))).sort();
  
  const filteredComponents = components.filter(component => {
    const matchesCategory = activeCategory === "all" || component.category === activeCategory;
    const matchesSearch = component.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (component.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesComplexity = complexityFilter === 'all' || component.complexity === complexityFilter;
    const matchesTag = !activeTagFilter || (component.tags || []).includes(activeTagFilter);
    const matchesFavorites = !showFavoritesOnly || favorites.includes(component.id);
    
    return matchesCategory && matchesSearch && matchesComplexity && matchesTag && matchesFavorites;
  });

  const visibleComponents = showAllComponents || activeCategory !== "all" 
    ? filteredComponents 
    : filteredComponents.slice(0, MAX_VISIBLE_COMPONENTS);

  const visibleTags = showAllTags 
    ? allTags 
    : allTags.slice(0, MAX_VISIBLE_TAGS);

  // Calculate tag counts for filtering
  const tagCounts = allTags.reduce((acc, tag) => {
    acc[tag] = components.filter(component => component.tags.includes(tag)).length;
    return acc;
  }, {} as Record<string, number>);
  
  const handleComponentClick = (component: ComponentItem | AppUiTemplate) => {
    // Check if user is authenticated or can use as guest
    if (user) {
      // Authenticated users always have access
    } else if (hasUsedTool('componentLibrary')) {
      // Guest has already used the tool once, require login
      setIsLoginPromptOpen(true);
      return;
    }
    // Otherwise, this is the first use for the guest
    
    if ('preview' in component) {
      setSelectedTemplate(component as AppUiTemplate);
      setShowPreview(true);
    } else {
      // Handle regular component click
      setSelectedComponent(component as ComponentItem);
      setShowComponentDetails(true);
    }
    
    // This is a meaningful action, mark the tool as used for guests
    if (!user) {
      markToolAsUsed('componentLibrary');
    }
  };
  
  // Function to handle template actions with feedback
  const handleTemplateAction = (templateId: string, action: 'canvas') => {
    // Check if user is authenticated or can use as guest
    if (user) {
      // Authenticated users always have access
    } else if (hasUsedTool('componentLibrary')) {
      // Guest has already used the tool once, require login
      setIsLoginPromptOpen(true);
      return;
    }
    // Otherwise, this is the first use for the guest
    
    // Find the selected template by ID
    const template = [...components, ...appUiTemplates].find(t => t.id === templateId);
    
    if (!template) {
      toast.error("Template not found");
      return;
    }
    
    // Determine component category/type based on the template type
    let componentType = 'ui';
    
    // Check if template is from components or appUiTemplates
    if ('category' in template) {
      // It's a ComponentItem
      componentType = template.category;
    } else if ('categories' in template && template.categories.length > 0) {
      // It's an AppUiTemplate
      componentType = template.categories[0];
    }
    
    // Get access to the current active canvas instance
    // This requires us to communicate with the Canvas component
    // We'll use a custom event for this communication
    const event = new CustomEvent('vibecoding:add-to-canvas', { 
      detail: { 
        templateId, 
        previewHtml: template.preview,
        componentName: template.name,
        componentType: componentType,
        componentComplexity: template.complexity || 'medium'
      } 
    });
    document.dispatchEvent(event);
    
    // Show success message in a consistent way with ComponentDialog.tsx
    toast.success("Template added to Visual Canvas!", {
      position: "bottom-right",
      style: {
        zIndex: 10000,
        background: "rgba(34, 197, 94, 0.9)", 
        color: "white"
      }
    });
    
    // Close the preview dialog
    setShowPreview(false);
    
    // This is a meaningful action, mark the tool as used for guests
    if (!user) {
      markToolAsUsed('componentLibrary');
    }
  };
  
  const filteredAppTemplates = appUiTemplates.filter(template => {
    // Check if template matches the category filter
    const categoryMatch =
      activeAppCategory === "all" ||
      template.categories.some(cat => 
        cat.toLowerCase() === activeAppCategory.toLowerCase() ||
        appCategories.find(c => c.id === activeAppCategory)?.name.toLowerCase() === cat.toLowerCase()
      );

    // Check if template matches the pattern filter
    const patternMatch =
      activePattern === "all" ||
      template.patterns.some(
        pattern => pattern.toLowerCase() === activePattern.toLowerCase()
      );

    // Check if template matches the search query
    const searchMatch =
      appSearchQuery === "" ||
      template.name.toLowerCase().includes(appSearchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(appSearchQuery.toLowerCase());

    // Check if template matches the complexity filter
    const complexityMatch = 
      complexityFilter === 'all' || 
      template.complexity === complexityFilter;

    // Check if template is in favorites (if favorites filter is on)
    const matchesFavorites = !showFavoritesOnly || favorites.includes(template.id);

    return categoryMatch && patternMatch && searchMatch && complexityMatch && matchesFavorites;
  });
  
  // Update visibleAppTemplates to only show 6 on All Apps
  const MAX_VISIBLE_APP_TEMPLATES = 6;
  const visibleAppTemplates = activeAppCategory === "all" 
    ? (showAllComponents ? filteredAppTemplates : filteredAppTemplates.slice(0, MAX_VISIBLE_APP_TEMPLATES))
    : filteredAppTemplates;
  
  return (
    <section id="component-library-content">
      <Dialog open={isLoginPromptOpen} onOpenChange={setIsLoginPromptOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Sign In Required</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Please sign in to use the Component Library. This feature requires an account.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsLoginPromptOpen(false)}>
              Close
            </Button>
            <Button onClick={() => window.location.href = "/auth"}>
              Sign In
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 relative inline-block group">
            <span className="curved-underline">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-600 to-red-600">Component</span> <span className="text-white">Library</span>
            </span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            Build faster with pre-built components and templates. Drag, drop, or copy code snippets.
          </p>
        </div>

        <div className="w-full max-w-md mx-auto mb-8">
          <Tabs 
            defaultValue="components" 
            className="w-full"
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as 'components' | 'appUIs')}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="components" className="flex items-center gap-1.5">
                <Code size={14} /> Components
              </TabsTrigger>
              <TabsTrigger value="appUIs" className="flex items-center gap-1.5">
                <Layers size={14} /> App UIs
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {activeTab === 'components' && (
          <>
            <div className="flex flex-col md:flex-row justify-between gap-6 mb-8">
          <CategoryFilters categories={componentCategories} activeCategory={activeCategory} setActiveCategory={setActiveCategory} />
          
          <SearchAndViewOptions searchQuery={searchQuery} setSearchQuery={setSearchQuery} viewMode={viewMode} setViewMode={setViewMode} />
        </div>
            
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="w-full flex flex-wrap items-center justify-between gap-4">
                <div className="flex-wrap items-center flex gap-2 max-w-[70%]">
                  <span className="text-sm text-muted-foreground font-medium mr-1">Tags:</span>
                  <Badge 
                    variant={activeTagFilter === null ? "default" : "outline"} 
                    className="cursor-pointer px-4 py-1.5 rounded-md shadow-sm hover:shadow text-sm"
                    onClick={() => setActiveTagFilter(null)}
                  >
                    All
                  </Badge>
                  {visibleTags.map(tag => (
                    <Badge 
                      key={tag} 
                      variant={activeTagFilter === tag ? "default" : "outline"} 
                      className={`cursor-pointer px-4 py-1.5 rounded-md shadow-sm hover:shadow text-sm ${
                        activeTagFilter === tag ? 'bg-primary/20 border-primary/30 text-primary' : 'hover:bg-secondary/20'
                      }`}
                      onClick={() => setActiveTagFilter(tag)}
                    >
                      {tag}
                    </Badge>
                  ))}
                  {allTags.length > MAX_VISIBLE_TAGS && !showAllTags && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setShowAllTags(true)}
                    >
                      Show More Tags
                    </Button>
                  )}
                  {showAllTags && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setShowAllTags(false)}
                    >
                      Show Less Tags
                    </Button>
                  )}
                </div>
                
                <div className="flex items-center gap-3 flex-shrink-0">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-9 px-4 flex items-center gap-2">
                        <span>Complexity: </span>
                        <span className="font-medium">{complexityFilter === 'all' ? 'All' : 
                          complexityFilter === 'simple' ? 'Simple' : 
                          complexityFilter === 'medium' ? 'Medium' : 'Complex'}</span>
                        <ChevronDown size={14} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[180px]">
                      <DropdownMenuItem 
                        className={`flex items-center gap-2 ${complexityFilter === 'all' ? 'bg-primary/10 text-primary font-medium' : ''}`}
                        onClick={() => setComplexityFilter('all')}
                      >
                        All
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className={`flex items-center gap-2 ${complexityFilter === 'simple' ? 'bg-primary/10 text-primary font-medium' : ''}`}
                        onClick={() => setComplexityFilter('simple')}
                      >
                        Simple
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className={`flex items-center gap-2 ${complexityFilter === 'medium' ? 'bg-primary/10 text-primary font-medium' : ''}`}
                        onClick={() => setComplexityFilter('medium')}
                      >
                        Medium
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className={`flex items-center gap-2 ${complexityFilter === 'complex' ? 'bg-primary/10 text-primary font-medium' : ''}`}
                        onClick={() => setComplexityFilter('complex')}
                      >
                        Complex
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  {user && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                      className={`h-9 px-4 flex items-center gap-2 ${showFavoritesOnly ? 'bg-primary/20 text-primary border-primary/30' : ''}`}
                    >
                      <Bookmark size={16} className={showFavoritesOnly ? "fill-primary" : ""} />
                      <span>Favorites</span>
                      {showFavoritesOnly && (
                        <Badge variant="secondary" className="ml-1 bg-primary/30">on</Badge>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="md:hidden flex flex-wrap items-center gap-2 mb-6">
              <div className="w-full flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground font-medium mr-1">Tags:</span>
                <Badge 
                  variant={activeTagFilter === null ? "default" : "outline"} 
                  className="cursor-pointer px-4 py-1.5 rounded-md shadow-sm hover:shadow text-sm"
                  onClick={() => setActiveTagFilter(null)}
                >
                  All
                </Badge>
                {visibleTags.map(tag => (
                  <Badge 
                    key={tag} 
                    variant={activeTagFilter === tag ? "default" : "outline"} 
                    className={`cursor-pointer px-4 py-1.5 rounded-md shadow-sm hover:shadow text-sm ${
                      activeTagFilter === tag ? 'bg-primary/20 border-primary/30 text-primary' : 'hover:bg-secondary/20'
                    }`}
                    onClick={() => setActiveTagFilter(tag)}
                  >
                    {tag}
                  </Badge>
                ))}
                {allTags.length > MAX_VISIBLE_TAGS && !showAllTags && (
                  <Badge 
                    variant="outline" 
                    className="cursor-pointer flex items-center gap-1 px-4 py-1.5 rounded-md bg-secondary/10 hover:bg-secondary/30 shadow-sm hover:shadow text-sm"
                    onClick={() => setShowAllTags(true)}
                  >
                    <Plus size={14} />
                    {allTags.length - MAX_VISIBLE_TAGS} more
                  </Badge>
                )}
                {showAllTags && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="h-8 px-3 text-sm text-muted-foreground hover:text-foreground"
                    onClick={() => setShowAllTags(false)}
                  >
                    Show less
                  </Button>
                )}
              </div>
        </div>

        <div className={`grid gap-6 ${viewMode === "grid" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1"}`}>
              {visibleComponents.map(component => (
            <ComponentCard 
              key={component.id} 
              component={component} 
              viewMode={viewMode} 
              animateCards={animateCards} 
              copied={copied} 
              setCopied={setCopied} 
              onOpenDetails={() => handleComponentClick(component)} 
                  isFavorite={favorites.includes(component.id)}
                  onToggleFavorite={(e) => toggleFavorite(component.id, e)}
                  previewMode="dark"
                  isLoggedIn={!!user}
            />
          ))}
        </div>

            {activeCategory === "all" && filteredComponents.length > MAX_VISIBLE_COMPONENTS && !showAllComponents && (
              <div className="flex justify-center mt-8">
                <Button 
                  variant="outline" 
                  size="lg" 
                  onClick={() => setShowAllComponents(true)}
                  className="flex items-center gap-2"
                >
                  See More <ChevronDown size={16} />
                </Button>
              </div>
            )}

            {activeCategory === "all" && showAllComponents && (
              <div className="flex justify-center mt-8">
                <Button 
                  variant="outline" 
                  size="lg" 
                  onClick={() => setShowAllComponents(false)}
                  className="flex items-center gap-2"
                >
                  See Less <ChevronDown size={16} className="rotate-180" />
                </Button>
              </div>
            )}

            <ComponentDialog 
              component={selectedComponent} 
              onClose={() => setSelectedComponent(null)} 
              copied={copied} 
              setCopied={setCopied} 
              previewMode="dark"
              isFavorite={selectedComponent ? favorites.includes(selectedComponent.id) : false}
              onToggleFavorite={() => selectedComponent && toggleFavorite(selectedComponent.id)}
            />
        
        {filteredComponents.length === 0 && (
          <div className="text-center py-12 border border-primary/20 rounded-lg bg-black/30 backdrop-blur-sm">
            <p className="text-muted-foreground mb-4">No components found matching your criteria.</p>
            <Button variant="outline" onClick={() => {
              setActiveCategory("all");
              setSearchQuery("");
                  setComplexityFilter('all');
                  setActiveTagFilter(null);
                  setShowFavoritesOnly(false);
            }}>
              Reset Filters
            </Button>
          </div>
        )}
          </>
        )}

        {activeTab === 'appUIs' && (
          <>
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-2 flex-wrap">
                {appCategories.slice(0, showAllAppCategories ? undefined : 5).map(category => (
                  <CategoryButton
                    key={category.id}
                    category={category}
                    isActive={activeAppCategory === category.id}
                    onClick={() => setActiveAppCategory(category.id)}
                  >
                    {category.name}
                  </CategoryButton>
                ))}
                {appCategories.length > 5 && !showAllAppCategories && (
                  <CategoryButton
                    isActive={false}
                    onClick={() => setShowAllAppCategories(true)}
                  >
                    <div className="flex items-center gap-1.5">
                      <Plus size={14} className="opacity-80" />
                      More
      </div>
                  </CategoryButton>
                )}
                {showAllAppCategories && (
                  <CategoryButton
                    isActive={false}
                    onClick={() => setShowAllAppCategories(false)}
                  >
                    Less
                  </CategoryButton>
                )}
              </div>
              
              <div className="flex gap-3 items-center">
                <div className="relative w-full sm:w-auto">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search app templates..."
                    className="pl-9 w-full sm:w-[250px] bg-black/50 border-white/10 focus:border-primary/40 rounded-md"
                    value={appSearchQuery}
                    onChange={(e) => setAppSearchQuery(e.target.value)}
                  />
                  {appSearchQuery && (
                    <button
                      onClick={() => setAppSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <div className="border border-white/10 rounded-md flex bg-black/50 overflow-hidden">
                  <button
                    onClick={() => setAppViewMode("grid")}
                    className={`p-2 transition-colors ${appViewMode === "grid" ? "bg-primary/20 text-primary" : "hover:bg-white/5 text-muted-foreground hover:text-foreground"}`}
                    title="Grid view"
                  >
                    <Grid size={18} />
                  </button>
                  <button
                    onClick={() => setAppViewMode("list")}
                    className={`p-2 transition-colors ${appViewMode === "list" ? "bg-primary/20 text-primary" : "hover:bg-white/5 text-muted-foreground hover:text-foreground"}`}
                    title="List view"
                  >
                    <List size={18} />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="w-full flex flex-wrap items-center justify-between gap-4">
                <div className="flex-wrap items-center flex gap-2 max-w-[70%]">
                  <span className="text-sm text-muted-foreground font-medium mr-1">Tags:</span>
                  <Badge 
                    variant={activePattern === "all" ? "default" : "outline"} 
                    className="cursor-pointer px-4 py-1.5 rounded-md shadow-sm hover:shadow text-sm"
                    onClick={() => setActivePattern("all")}
                  >
                    All
                  </Badge>
                  {UI_PATTERNS.slice(0, showAllAppPatterns ? undefined : 5).map(pattern => (
                    <Badge 
                      key={pattern}
                      variant={activePattern === pattern ? "default" : "outline"} 
                      className={`cursor-pointer px-4 py-1.5 rounded-md shadow-sm hover:shadow text-sm ${
                        activePattern === pattern ? 'bg-primary/20 border-primary/30 text-primary' : 'hover:bg-secondary/20'
                      }`}
                      onClick={() => setActivePattern(pattern)}
                    >
                      {pattern}
                    </Badge>
                  ))}
                  {UI_PATTERNS.length > 5 && !showAllAppPatterns && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setShowAllAppPatterns(true)}
                    >
                      Show More Tags
                    </Button>
                  )}
                  {showAllAppPatterns && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setShowAllAppPatterns(false)}
                    >
                      Show Less Tags
                    </Button>
                  )}
                </div>
                
                <div className="flex items-center gap-3 flex-shrink-0">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-9 px-4 flex items-center gap-2">
                        <span>Complexity: </span>
                        <span className="font-medium">{complexityFilter === 'all' ? 'All' : 
                          complexityFilter === 'simple' ? 'Simple' : 
                          complexityFilter === 'medium' ? 'Medium' : 'Complex'}</span>
                        <ChevronDown size={14} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[180px]">
                      <DropdownMenuItem 
                        className={`flex items-center gap-2 ${complexityFilter === 'all' ? 'bg-primary/10 text-primary font-medium' : ''}`}
                        onClick={() => setComplexityFilter('all')}
                      >
                        All
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className={`flex items-center gap-2 ${complexityFilter === 'simple' ? 'bg-primary/10 text-primary font-medium' : ''}`}
                        onClick={() => setComplexityFilter('simple')}
                      >
                        Simple
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className={`flex items-center gap-2 ${complexityFilter === 'medium' ? 'bg-primary/10 text-primary font-medium' : ''}`}
                        onClick={() => setComplexityFilter('medium')}
                      >
                        Medium
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className={`flex items-center gap-2 ${complexityFilter === 'complex' ? 'bg-primary/10 text-primary font-medium' : ''}`}
                        onClick={() => setComplexityFilter('complex')}
                      >
                        Complex
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  {user && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                      className={`h-9 px-4 flex items-center gap-2 ${showFavoritesOnly ? 'bg-primary/20 text-primary border-primary/30' : ''}`}
                    >
                      <Bookmark size={16} className={showFavoritesOnly ? "fill-primary" : ""} />
                      <span>Favorites</span>
                      {showFavoritesOnly && (
                        <Badge variant="secondary" className="ml-1 bg-primary/30">on</Badge>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="md:hidden flex flex-wrap items-center gap-2 mb-6">
              <div className="w-full flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground font-medium mr-1">Tags:</span>
                <Badge 
                  variant={activePattern === "all" ? "default" : "outline"} 
                  className="cursor-pointer px-4 py-1.5 rounded-md shadow-sm hover:shadow text-sm"
                  onClick={() => setActivePattern("all")}
                >
                  All
                </Badge>
                {UI_PATTERNS.slice(0, 3).map(pattern => (
                  <Badge 
                    key={pattern} 
                    variant={activePattern === pattern ? "default" : "outline"} 
                    className={`cursor-pointer px-4 py-1.5 rounded-md shadow-sm hover:shadow text-sm ${
                      activePattern === pattern ? 'bg-primary/20 border-primary/30 text-primary' : 'hover:bg-secondary/20'
                    }`}
                    onClick={() => setActivePattern(pattern)}
                  >
                    {pattern}
                  </Badge>
                ))}
                {UI_PATTERNS.length > 3 && (
                  <Badge 
                    variant="outline" 
                    className="cursor-pointer flex items-center gap-1 px-4 py-1.5 rounded-md bg-secondary/10 hover:bg-secondary/30 shadow-sm hover:shadow text-sm"
                    onClick={() => setShowAllAppPatterns(!showAllAppPatterns)}
                  >
                    {showAllAppPatterns ? 'Show Less' : (
                      <>
                        <Plus size={14} />
                        {UI_PATTERNS.length - 3} more
                      </>
                    )}
                  </Badge>
                )}
              </div>
            </div>
            
            <div className={`grid gap-6 ${appViewMode === "grid" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1"}`}>
              {visibleAppTemplates.map(template => (
                <Card
                  key={template.id}
                  onClick={() => handleComponentClick(template)}
                  className={`overflow-hidden h-full transition-all duration-300 ${
                    animateCards ? "animate-in fade-in zoom-in-95" : ""
                  } ${appViewMode === "list" ? "flex" : "flex flex-col"} 
                  group hover:border-primary/40 hover:shadow-md hover:shadow-primary/10 hover:bg-secondary/30 cursor-pointer`}
                >
                  <div 
                    className={`${appViewMode === "list" ? "w-1/3 border-r border-border" : "w-full border-b border-border"} bg-black/30 relative overflow-hidden group-hover:border-primary/20`}
                  >
                    <div 
                      className={`relative aspect-video flex items-center justify-center overflow-hidden transition-colors bg-black/50`}
                    >
                      <div 
                        dangerouslySetInnerHTML={{ __html: template.preview }} 
                        className="w-full h-full flex items-center justify-center scale-90 transition-transform duration-300 group-hover:scale-95"
                        onClick={(e) => {
                          // Prevent default behavior and propagation
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      />
                    </div>
                  </div>
                  <CardContent className={`p-4 flex flex-col justify-between flex-grow ${appViewMode === "list" ? "w-2/3" : "w-full"}`}>
                    <div>
                      <h3 className="font-medium mb-1 group-hover:text-primary transition-colors">{template.name}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 min-h-[40px]">
                        {template.description || `A ${template.complexity || 'medium'} UI template for ${template.categories[0] || 'your app'}.`}
                      </p>
                    </div>
                    
                    <div className="mt-auto">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          {template.categories && template.categories.slice(0, 1).map((category, index) => (
                            <span 
                              key={`${template.id}-${category}-${index}`} 
                              className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full transition-colors hover:bg-primary/20"
                            >
                              {category}
                            </span>
                          ))}
                          {template.complexity && (
                            <CategoryBadge 
                              className={`text-xs ${
                                template.complexity === 'simple' ? 'bg-green-500/10 text-green-500' :
                                template.complexity === 'medium' ? 'bg-amber-500/10 text-amber-500' :
                                template.complexity === 'complex' ? 'bg-red-500/10 text-red-500' :
                                'bg-amber-500/10 text-amber-500'
                              }`}
                            >
                              {template.complexity.charAt(0).toUpperCase() + template.complexity.slice(1)}
                            </CategoryBadge>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {user && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                toggleFavorite(template.id, e);
                              }}
                              className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full transition-all hover:scale-110 active:scale-95"
                              title={favorites.includes(template.id) ? "Remove from favorites" : "Add to favorites"}
                            >
                              {favorites.includes(template.id) ? <Bookmark size={16} className="fill-primary text-primary" /> : <BookmarkPlus size={16} />}
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              navigator.clipboard.writeText(generateCompleteHtml(template));
                              setCopied(template.id);
                              toast.success("Complete HTML copied to clipboard!");
                              
                              setTimeout(() => {
                                setCopied(null);
                              }, 2000);
                            }}
                            className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full transition-all hover:scale-110 active:scale-95"
                            title="Copy HTML"
                          >
                            {copied === template.id ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                          </button>
                        </div>
                      </div>
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full text-xs hover:bg-primary/10 hover:text-primary transition-all hover:border-primary/30 active:scale-[0.98]" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleComponentClick(template);
                        }}
                      >
                        <Eye size={14} className="mr-1.5" />
                        Preview
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* "No templates found" message */}
            {filteredAppTemplates.length === 0 && (
              <div className="text-center py-12 border border-primary/20 rounded-lg bg-black/30 backdrop-blur-sm">
                <p className="text-muted-foreground mb-4">No app UI templates found matching your criteria.</p>
                <Button variant="outline" onClick={() => {
                  setActiveAppCategory("all");
                  setActivePattern("all");
                  setAppSearchQuery("");
                  setComplexityFilter('all');
                  setShowFavoritesOnly(false);
                }}>
                  Reset Filters
                </Button>
              </div>
            )}
            
            {/* Show "See More" button if needed */}
            {activeAppCategory === "all" && !appSearchQuery && complexityFilter === 'all' && !showFavoritesOnly && !showAllComponents && filteredAppTemplates.length > MAX_VISIBLE_APP_TEMPLATES && (
              <div className="flex justify-center mt-8">
                <Button 
                  variant="outline" 
                  size="lg" 
                  onClick={() => setShowAllComponents(true)}
                  className="flex items-center gap-2"
                >
                  See More <ChevronDown size={16} />
                </Button>
              </div>
            )}
            
            {/* Show "See Less" button if needed */}
            {activeAppCategory === "all" && !appSearchQuery && complexityFilter === 'all' && !showFavoritesOnly && showAllComponents && (
              <div className="flex justify-center mt-8">
                <Button 
                  variant="outline" 
                  size="lg" 
                  onClick={() => setShowAllComponents(false)}
                  className="flex items-center gap-2"
                >
                  See Less <ChevronDown size={16} className="rotate-180" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      {showPreview && selectedTemplate && (
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-5xl h-[90vh] overflow-hidden p-0 border border-primary/20 bg-black/80 backdrop-blur-xl">
            <div className="flex flex-col h-full">
              <div className="p-6 border-b border-border/40">
                <div className="mb-2 flex items-center justify-between">
                  <h2 className="text-xl font-semibold">{selectedTemplate.name}</h2>
                  {/* Removed category badges from here */}
                </div>
                <p className="text-sm text-muted-foreground mb-2">{selectedTemplate.description || `A ${selectedTemplate.complexity || 'medium'} UI template for ${selectedTemplate.categories ? selectedTemplate.categories[0] : 'your app'}.`}</p>
                <div className="flex items-center gap-2">
                  <CategoryBadge 
                    className={`text-xs ${
                      selectedTemplate.complexity === 'simple' ? 'bg-green-500/10 text-green-500' :
                      selectedTemplate.complexity === 'medium' ? 'bg-amber-500/10 text-amber-500' :
                      selectedTemplate.complexity === 'complex' ? 'bg-red-500/10 text-red-500' :
                      'bg-amber-500/10 text-amber-500'
                    }`}
                  >
                    {selectedTemplate.complexity || 'Medium'} complexity
                  </CategoryBadge>
                  
                  {/* Display categories here instead, below the description */}
                  {selectedTemplate.categories && selectedTemplate.categories.map((category, index) => (
                    <CategoryBadge 
                      key={`preview-${category}-${index}`}
                      className="text-xs bg-primary/5 border-primary/20 text-primary/80 transition-colors"
                    >
                      {category}
                    </CategoryBadge>
                  ))}
                </div>
              </div>
              <div className="flex-1 overflow-auto p-6 relative">
                <div 
                  className="w-full bg-background rounded-lg border border-border flex items-center justify-center overflow-hidden shadow-sm transition-all duration-300 hover:shadow-md hover:shadow-primary/5 hover:border-primary/20"
                  style={{ height: "100%" }}
                >
                  <div
                    dangerouslySetInnerHTML={{ __html: selectedTemplate.preview }}
                    className="w-full h-full flex items-center justify-center scale-[0.90] transition-transform duration-300 hover:scale-[0.92]"
                    data-editable-component="true"
                    data-component-id={selectedTemplate.id}
                    data-component-name={selectedTemplate.name}
                    onClick={(e) => {
                      // Prevent default behavior for all clicks within the preview
                      e.preventDefault();
                      
                      // Mark clicked elements with data-selected attribute for highlighting
                      const target = e.target as HTMLElement;
                      if (target && target !== e.currentTarget) {
                        // Remove previous selections
                        document.querySelectorAll('[data-selected="true"]').forEach(el => {
                          el.removeAttribute('data-selected');
                        });
                        
                        // Add selected attribute
                        target.setAttribute('data-selected', 'true');
                        
                        // Apply temporary highlight style
                        target.style.outline = '2px solid #3b82f6';
                        setTimeout(() => {
                          target.style.outline = '';
                        }, 1000);
                      }
                    }}
                  />
                </div>
                
                <div className="absolute top-9 right-9">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex items-center gap-1.5 bg-background/80 backdrop-blur-sm border-border hover:bg-primary/10 hover:text-primary transition-all hover:border-primary/30 active:scale-[0.98]"
                    onClick={() => handleTemplateAction(selectedTemplate.id, 'canvas')}
                  >
                    <Layout size={14} className="mr-1" />
                    Use in Canvas
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
};

export default ComponentLibrary;
